import { store, getContext, getElement } from '@wordpress/interactivity';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import 'leaflet-gesture-handling';

const MORE_INFO_LABEL = "Plus d'infos";

function getMoreInfoLabel(context) {
    const label = context?.moreInfoLabel;
    if (typeof label === 'string' && label.trim() !== '') return label;
    return MORE_INFO_LABEL;
}

function getTileLayerUrl(mapStyle) {
    const tileLayers = {
        'light': 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        'dark': 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        'satellite': 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        'terrain': 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
        'voyager': 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
    };
    return tileLayers[mapStyle] || tileLayers['light'];
}

function getTileLayerAttribution(mapStyle) {
    const attributions = {
        'light': '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
        'dark': '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
        'satellite': '&copy; <a href="https://www.esri.com/">Esri</a>',
        'terrain': '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
        'voyager': '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
    };
    return attributions[mapStyle] || attributions['light'];
}

// Fonction pour ajouter les polygones des départements de la région Grand Est
async function addGrandEstDepartments(map) {
    // Style pour les départements
    const departmentStyle = {
        color: '#cea575',      // Couleur du stroke (beige/doré)
        fillColor: '#cea575',  // Couleur du fill (beige/doré)
        fillOpacity: 0.2,      // Opacité du fill (20%)
        weight: 2,             // Épaisseur du stroke
        opacity: 0.8           // Opacité du stroke (80%)
    };

    // Codes des départements de la région Grand Est
    const grandEstDepartments = ['08', '10', '51', '52', '54', '55', '57', '67', '68', '88'];

    try {
        // Charger les données GeoJSON depuis une API publique
        // Utilisation de l'API de data.gouv.fr pour les contours des départements
        const response = await fetch('https://raw.githubusercontent.com/gregoiredavid/france-geojson/master/departements.geojson');
        const data = await response.json();

        // Filtrer uniquement les départements du Grand Est
        const grandEstFeatures = data.features.filter(feature => {
            const codeDept = feature.properties.code;
            return grandEstDepartments.includes(codeDept);
        });

        // Créer un GeoJSON avec uniquement les départements du Grand Est
        const grandEstGeoJSON = {
            type: 'FeatureCollection',
            features: grandEstFeatures
        };

        // Ajouter les polygones à la carte avec gestion du clic
        L.geoJSON(grandEstGeoJSON, {
            style: departmentStyle,
            onEachFeature: (feature, layer) => {
                const deptCode = feature.properties.code;
                try {
                    const bounds = layer.getBounds();
                    const center = bounds.getCenter();
                    if (state && state.departmentCenters) {
                        state.departmentCenters[deptCode] = [center.lat, center.lng];
                    }
                    if (state && state.departmentClusters && state.departmentClusters[deptCode]) {
                        state.departmentClusters[deptCode].setLatLng([center.lat, center.lng]);
                    }
                } catch (e) {
                    // no-op: on garde le fallback statique si nécessaire
                }
                // Rendre le polygone cliquable pour zoomer sur le département
                layer.on('click', () => {
                    // Compter les campings de ce département
                    const campings = state.campings.filter(c => getDepartmentCode(c) === deptCode);
                    if (campings.length > 0) {
                        store('gt/ctv').actions.zoomToDepartment(deptCode, campings);
                    }
                });
                // Changer le curseur au survol
                layer.on('mouseover', () => {
                    layer.setStyle({
                        fillOpacity: 0.3,
                        weight: 3
                    });
                });
                layer.on('mouseout', () => {
                    layer.setStyle(departmentStyle);
                });
            }
        }).addTo(map);

    } catch (error) {
        console.error('Erreur lors du chargement des données GeoJSON des départements:', error);
        // En cas d'erreur, ne rien afficher plutôt que d'afficher des données incorrectes
    }
}

// Fonction pour créer un marqueur de cluster département (cercle avec nombre)
function createDepartmentClusterIcon(count, color = '#cea575') {
    const size = 50;
    const fontSize = count > 99 ? 16 : 20;
    return L.divIcon({
        html: `
            <div style="
                width: ${size}px;
                height: ${size}px;
                border-radius: 50%;
                background-color: ${color};
                border: 3px solid #fff;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-weight: bold;
                font-size: ${fontSize}px;
                font-family: Arial, sans-serif;
            ">${count}</div>
        `,
        className: 'department-cluster-marker',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
    });
}

// Fonction pour créer un cluster de pins proches (cercle avec nombre, plus petit)
function createPinClusterIcon(count, color = '#3498db') {
    const size = 40;
    const fontSize = count > 99 ? 14 : 18;
    return L.divIcon({
        html: `
            <div style="
                width: ${size}px;
                height: ${size}px;
                border-radius: 50%;
                background-color: ${color};
                border: 2px solid #fff;
                box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-weight: bold;
                font-size: ${fontSize}px;
                font-family: Arial, sans-serif;
                cursor: pointer;
            ">${count}</div>
        `,
        className: 'pin-cluster-marker',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
    });
}

// Fonction pour regrouper les pins proches en clusters (basée sur la distance en mètres)
function clusterPins(campings, markers, maxDistanceMeters = 2000) {
    const clusters = [];
    const used = new Set();
    
    campings.forEach((camping, index) => {
        if (used.has(camping.idSite)) return;
        
        const marker = markers[camping.idSite];
        if (!marker) return;
        
        const { latitude, longitude } = camping.gpsCoordinates;
        const point1 = L.latLng(latitude, longitude);
        const cluster = {
            campings: [camping],
            center: [latitude, longitude],
            bounds: L.latLngBounds([[latitude, longitude]])
        };
        
        // Chercher les pins proches (distance en mètres)
        campings.forEach((otherCamping, otherIndex) => {
            if (otherIndex === index || used.has(otherCamping.idSite)) return;
            
            const otherMarker = markers[otherCamping.idSite];
            if (!otherMarker) return;
            
            const { latitude: lat2, longitude: lon2 } = otherCamping.gpsCoordinates;
            const point2 = L.latLng(lat2, lon2);
            const distance = point1.distanceTo(point2); // Distance en mètres
            
            if (distance < maxDistanceMeters) {
                cluster.campings.push(otherCamping);
                cluster.bounds.extend([lat2, lon2]);
                used.add(otherCamping.idSite);
            }
        });
        
        used.add(camping.idSite);
        
        // Calculer le centre du cluster
        if (cluster.campings.length > 1) {
            const center = cluster.bounds.getCenter();
            cluster.center = [center.lat, center.lng];
        }
        
        clusters.push(cluster);
    });
    
    return clusters;
}

// Fonction pour obtenir le centre approximatif d'un département
function getDepartmentCenter(code) {
    const centers = {
        '08': [49.7, 4.7],   // Ardennes
        '10': [48.3, 4.1],   // Aube
        '51': [49.0, 4.4],   // Marne
        '52': [48.1, 5.1],   // Haute-Marne
        '54': [48.7, 6.2],   // Meurthe-et-Moselle
        '55': [49.1, 5.4],   // Meuse
        '57': [49.1, 6.2],   // Moselle
        '67': [48.6, 7.7],   // Bas-Rhin
        '68': [47.8, 7.3],   // Haut-Rhin
        '88': [48.2, 6.5]    // Vosges
    };
    return centers[code] || [48.5, 6.0]; // Centre par défaut de la région
}

function getDepartmentClusterCenter(campings, deptCode) {
    if (state && state.departmentCenters && state.departmentCenters[deptCode]) {
        return state.departmentCenters[deptCode];
    }

    if (Array.isArray(campings) && campings.length > 0) {
        const points = campings
            .map((camping) => camping?.gpsCoordinates)
            .filter((gps) => gps && gps.latitude !== null && gps.longitude !== null)
            .map((gps) => [parseFloat(gps.latitude), parseFloat(gps.longitude)])
            .filter(([lat, lng]) => !Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0);

        if (points.length > 0) {
            const bounds = L.latLngBounds(points);
            const center = bounds.getCenter();
            return [center.lat, center.lng];
        }
    }

    // Fallback si aucun GPS exploitable.
    return getDepartmentCenter(deptCode);
}

// Fonction pour extraire le code département depuis le code postal ou la région
function getDepartmentCode(camping) {
    // Debug: logger les données du camping
    if (!camping) {
        console.warn('getDepartmentCode: camping est null/undefined');
        return null;
    }
    
    // Essayer d'abord depuis le champ department (code à 2 chiffres)
    if (camping.department) {
        const deptCode = String(camping.department).trim();
        const grandEstCodes = ['08', '10', '51', '52', '54', '55', '57', '67', '68', '88'];
        if (grandEstCodes.includes(deptCode)) {
            return deptCode;
        }
    }
    
    // Essayer depuis administrativeZone2 (code département)
    if (camping.administrativeZone2) {
        const deptCode = String(camping.administrativeZone2).trim();
        const grandEstCodes = ['08', '10', '51', '52', '54', '55', '57', '67', '68', '88'];
        if (grandEstCodes.includes(deptCode)) {
            return deptCode;
        }
    }
    
    // Essayer d'extraire depuis le code postal
    if (camping.postalCode) {
        const postalCode = String(camping.postalCode).padStart(5, '0');
        const deptCode = postalCode.substring(0, 2);
        // Vérifier si c'est un département du Grand Est
        const grandEstCodes = ['08', '10', '51', '52', '54', '55', '57', '67', '68', '88'];
        if (grandEstCodes.includes(deptCode)) {
            return deptCode;
        }
    }
    
    // Sinon, essayer depuis la région
    if (camping.region) {
        // Mapper les noms de régions aux codes départements (approximatif)
        const region = String(camping.region).toLowerCase();
        if (region.includes('alsace') || region.includes('bas-rhin')) return '67';
        if (region.includes('haut-rhin')) return '68';
        if (region.includes('lorraine') || region.includes('moselle')) return '57';
        if (region.includes('meurthe')) return '54';
        if (region.includes('meuse')) return '55';
        if (region.includes('vosges')) return '88';
        if (region.includes('champagne') || region.includes('marne')) return '51';
        if (region.includes('haute-marne')) return '52';
        if (region.includes('aube')) return '10';
        if (region.includes('ardennes')) return '08';
    }
    
    return null;
}

function createModernSVGMarker(color, size = 20, style = 'circle') {
    const baseSize = size * 2;
    
    switch(style) {
        case 'square':
            return `
                <svg xmlns="http://www.w3.org/2000/svg" width="${baseSize}" height="${baseSize}" viewBox="0 0 ${baseSize} ${baseSize}">
                    <defs>
                        <filter id="shadow-square" x="-20%" y="-20%" width="140%" height="140%">
                            <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.2"/>
                        </filter>
                    </defs>
                    <rect x="${size * 0.5}" y="${size * 0.5}" width="${size}" height="${size}" fill="${color}" stroke="#FFF" stroke-width="2" filter="url(#shadow-square)"/>
                </svg>
            `;
            
        case 'diamond':
            return `
                <svg xmlns="http://www.w3.org/2000/svg" width="${baseSize}" height="${baseSize}" viewBox="0 0 ${baseSize} ${baseSize}">
                    <defs>
                        <filter id="shadow-diamond" x="-20%" y="-20%" width="140%" height="140%">
                            <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.2"/>
                        </filter>
                    </defs>
                    <polygon points="${size},${size * 0.3} ${size * 1.7},${size} ${size},${size * 1.7} ${size * 0.3},${size}" fill="${color}" stroke="#FFF" stroke-width="2" filter="url(#shadow-diamond)"/>
                </svg>
            `;
            
        case 'star':
            return `
                <svg xmlns="http://www.w3.org/2000/svg" width="${baseSize}" height="${baseSize}" viewBox="0 0 ${baseSize} ${baseSize}">
                    <defs>
                        <filter id="shadow-star" x="-20%" y="-20%" width="140%" height="140%">
                            <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.2"/>
                        </filter>
                    </defs>
                    <polygon points="${size},${size * 0.2} ${size * 1.2},${size * 0.8} ${size * 1.8},${size * 0.8} ${size * 1.3},${size * 1.4} ${size * 1.6},${size * 1.8} ${size},${size * 1.2} ${size * 0.4},${size * 1.8} ${size * 0.7},${size * 1.4} ${size * 0.2},${size * 0.8} ${size * 0.8},${size * 0.8}" fill="${color}" stroke="#FFF" stroke-width="2" filter="url(#shadow-star)"/>
                </svg>
            `;
            
            case 'pin':
                return `
                    <svg xmlns="http://www.w3.org/2000/svg" width="${baseSize}" height="${baseSize}" viewBox="0 0 37 48">
                        <defs>
                            <filter id="shadow-pin" x="-20%" y="-20%" width="140%" height="140%">
                                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.2"/>
                            </filter>
                        </defs>
                        <!-- Pin exact de votre SVG -->
                        <path d="M18.4995 0.2854C8.2968 0.2854 -0.00427246 8.5861 -0.00427246 18.7903C-0.00427246 28.7844 17.2001 45.9947 17.9337 46.7226L18.4996 47.2854L19.067 46.7226C19.7992 45.9947 37.0045 28.7844 37.0045 18.7903C37.0044 8.5861 28.7037 0.2854 18.4995 0.2854ZM18.4995 24.4259C14.0639 24.4259 10.4557 20.8164 10.4557 16.3825C10.4557 11.945 14.0639 8.33482 18.4995 8.33482C22.9365 8.33482 26.5452 11.945 26.5452 16.3825C26.5452 20.8164 22.9365 24.4259 18.4995 24.4259Z" fill="${color}" stroke="#FFF" stroke-width="1" filter="url(#shadow-pin)"/>
                    </svg>
                `;
            
        default: // circle
    return `
                <svg xmlns="http://www.w3.org/2000/svg" width="${baseSize}" height="${baseSize}" viewBox="0 0 ${baseSize} ${baseSize}">
            <defs>
                        <filter id="shadow-circle" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.2"/>
                </filter>
            </defs>
                    <circle cx="${size}" cy="${size}" r="${size * 0.5}" fill="${color}" stroke="#FFF" stroke-width="2" filter="url(#shadow-circle)"/>
        </svg>
    `;
    }
}

function getMarkerIconAnchor(markerStyle) {
    // Leaflet positionne le point selon `iconAnchor`.
    // Pour le style `pin`, la "pointe" du SVG est en bas => ancre en bas.
    return markerStyle === 'pin' ? [20, 40] : [20, 20];
}

// Fonction utilitaire pour valider et contraindre le zoom entre minZoom et maxZoom
function constrainZoom(zoom, minZoom, maxZoom) {
    return Math.max(minZoom, Math.min(maxZoom, zoom));
}

/**
 * Ajuste légèrement le zoom initial sur petits écrans.
 * Objectif: dézoomer d'1 niveau sous 600px de large (sans toucher au réglage éditeur).
 */
function getResponsiveInitialZoom(contextInitialZoom, minZoom, maxZoom) {
    const baseZoom = constrainZoom(contextInitialZoom || 6, minZoom, maxZoom);
    const shouldZoomOut = (typeof window !== 'undefined' && window.innerWidth < 600);
    const adjustedZoom = shouldZoomOut ? (baseZoom - 0.5) : baseZoom;
    return constrainZoom(adjustedZoom, minZoom, maxZoom);
}

function resetActiveElements(queryElement) {
    const postTemplate = queryElement.querySelector('.wp-block-post-template');
    if (postTemplate) postTemplate.classList.remove('active');
    queryElement.querySelectorAll('[data-gt-camping]').forEach(el => el.classList.remove('active'));
}

function activateCamping(queryElement, campingId) {
    const el = queryElement.querySelector(`[data-gt-camping="${campingId}"]`);
    if (el) el.classList.add('active');
    const details = queryElement.querySelector('.wp-block-post-template');
    if (details) details.classList.add('active');
}

function adjustMapHeight(map, queryElement) {
    const detailsElement = queryElement.querySelector('.wp-block-post-template');
    if (detailsElement) {
        const detailsHeight = detailsElement.scrollHeight;
        map.getContainer().style.height = `${detailsHeight}px`;
    }
    map.invalidateSize();
}

function resetMarkers(state, markerColor, markerStyle, map) {
    Object.values(state.markers).forEach(marker => {
        marker.setIcon(L.divIcon({
            html: createModernSVGMarker(markerColor, 20, markerStyle),
            className: 'modern-svg-marker',
            iconSize: [40, 40],
            iconAnchor: getMarkerIconAnchor(markerStyle),
        }));
    });
    // Ne pas dézoomer la carte - garder le zoom actuel
    setTimeout(() => map.invalidateSize(), 200);
}

function highlightMarker(state, campingId, markerColor, markerStyle, hoverColor, map) {
    
    const marker = state.markers[campingId];
    if (marker) {
        
        try {
            marker.setIcon(L.divIcon({
                html: createModernSVGMarker(hoverColor, 20, markerStyle),
                className: 'modern-svg-marker highlighted',
                iconSize: [40, 40],
                iconAnchor: getMarkerIconAnchor(markerStyle),
            }));
        } catch (error) {
            console.error('❌ Erreur lors de l\'application de la surbrillance:', error);
        }
    } else {
    }
}

function resetMarkerHighlight(state, campingId, markerColor, markerStyle, map) {
    
    const marker = state.markers[campingId];
    if (marker) {
        try {
            marker.setIcon(L.divIcon({
                html: createModernSVGMarker(markerColor, 20, markerStyle),
                className: 'modern-svg-marker',
                iconSize: [40, 40],
                iconAnchor: getMarkerIconAnchor(markerStyle),
            }));
        } catch (error) {
            console.error('❌ Erreur lors de la suppression de la surbrillance:', error);
        }
    } else {
    }
}

function selectCampingAndUpdateUI(state, camping, element, map) {
    if (!camping) return;
    state.selectedCamping = camping;

    const queryElement = element.ref.closest('.wp-block-query');
    resetActiveElements(queryElement);
    activateCamping(queryElement, camping.idSite);

    const marker = state.markers[camping.idSite];
    if (marker) {
        const minZoom = 5;
        const maxZoom = 12;
        const currentZoom = map.getZoom();
        // Zoom progressif et calibré : zoomer de 1 niveau à la fois jusqu'à maxZoom - 1
        const targetZoom = currentZoom < maxZoom - 1 ? Math.min(currentZoom + 1, maxZoom - 1) : maxZoom - 1;
        const zoomLevel = constrainZoom(targetZoom, minZoom, maxZoom);
        map.setView(marker.getLatLng(), zoomLevel, { animate: true, duration: 0.4 });
        marker.openPopup();
    }

    setTimeout(() => adjustMapHeight(map, queryElement), 200);
}

/**
 * Ouvre la popup au survol (desktop) sans casser le comportement au clic.
 * - Activé uniquement si le device supporte le hover (évite mobile/tablette).
 * - Ne ferme pas la popup si le camping est "sélectionné" (clic).
 * - Ajoute un léger délai de fermeture pour permettre de passer du marker à la popup.
 */
function enablePopupOnHover(marker, state, campingIdSite) {
    try {
        if (typeof window === 'undefined') return;
        const canHover = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
        if (!canHover) return;

        const campingIdStr = String(campingIdSite);
        let closeTimeout = null;

        const isSelected = () => String(state?.selectedCamping?.idSite ?? '') === campingIdStr;
        const clearClose = () => {
            if (closeTimeout) {
                clearTimeout(closeTimeout);
                closeTimeout = null;
            }
        };
        const scheduleClose = () => {
            clearClose();
            closeTimeout = setTimeout(() => {
                if (!isSelected()) {
                    try { marker.closePopup(); } catch (e) {}
                }
            }, 250);
        };

        marker.on('mouseover', () => {
            clearClose();
            try {
                if (!marker.isPopupOpen()) marker.openPopup();
            } catch (e) {}
        });

        marker.on('mouseout', () => {
            if (isSelected()) return;
            scheduleClose();
        });

        // Quand la popup s'ouvre, on permet de la survoler sans la fermer
        marker.on('popupopen', (e) => {
            clearClose();
            const popupEl = e?.popup?.getElement?.();
            if (!popupEl) return;
            if (popupEl.dataset && popupEl.dataset.gtPopupHoverBound === '1') return;
            if (popupEl.dataset) popupEl.dataset.gtPopupHoverBound = '1';

            popupEl.addEventListener('mouseenter', () => clearClose());
            popupEl.addEventListener('mouseleave', () => {
                if (isSelected()) return;
                scheduleClose();
            });
        });

        // Si l'utilisateur clique, on annule toute fermeture planifiée
        marker.on('click', () => clearClose());
        marker.on('popupclose', () => clearClose());
    } catch (e) {
        // fail-safe
    }
}

const { state } = store('gt/ctv', {
    state: {
        map: null,
        markers: {},
        markerStyles: {}, // Stocker les styles originaux des markers
        departmentClusters: {}, // Stocker les marqueurs de cluster par département
        departmentCenters: {}, // Centres réels issus des polygones GeoJSON
        pinClusters: {}, // Stocker les clusters de pins proches
        moreInfoLabel: null, // Libellé du lien "Plus d'infos" (traduisible via WPML/WPM)
        currentDepartment: null, // Département actuellement affiché en détail
        departmentZoomLevel: null, // Zoom au moment du clic sur le département
        pinClusterZoomLevel: null, // Zoom au moment du clic sur un cluster de pins
        initMap: false,
        currentInitialZoom: null, // Stocker le zoom initial actuel pour détecter les changements
        minZoom: null, // Stocker le zoom minimum
        maxZoom: null, // Stocker le zoom maximum
        previousZoom: null, // Stocker le zoom précédent pour détecter les changements de zoom
        _responsiveZoomListenerAdded: false,
        _allowImmediateMapInit: false,
        _deferredContext: null,
        _deferredElement: null,
        // Debug: vérifier les données au chargement
        _debugChecked: false
    },
    actions: {
        initMap: () => {
            if (state.initMap) return;
            if (!state._allowImmediateMapInit) {
                const context = getContext();
                const element = getElement();
                const mapContainerId = context?.mapId;
                const mapContainer = mapContainerId ? document.getElementById(mapContainerId) : null;

                const runDeferredInit = () => {
                    if (state.initMap || state._allowImmediateMapInit) return;
                    state._deferredContext = context;
                    state._deferredElement = element;
                    state._allowImmediateMapInit = true;
                    store('gt/ctv').actions.initMap();
                };

                const scheduleDeferredInit = () => {
                    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
                        window.requestIdleCallback(() => runDeferredInit(), { timeout: 1500 });
                    } else {
                        setTimeout(runDeferredInit, 300);
                    }
                };

                if (mapContainer && typeof window !== 'undefined') {
                    const rect = mapContainer.getBoundingClientRect();
                    const isNearViewport = rect.top < window.innerHeight + 300 && rect.bottom > -300;

                    if (!isNearViewport && typeof IntersectionObserver !== 'undefined') {
                        const observer = new IntersectionObserver((entries) => {
                            if (entries.some((entry) => entry.isIntersecting)) {
                                observer.disconnect();
                                scheduleDeferredInit();
                            }
                        }, {
                            root: null,
                            rootMargin: '300px 0px'
                        });

                        observer.observe(mapContainer);
                        return;
                    }
                }

                if (typeof window !== 'undefined' && document.readyState !== 'complete') {
                    window.addEventListener('load', scheduleDeferredInit, { once: true });
                    return;
                }

                scheduleDeferredInit();
                return;
            }

            state._allowImmediateMapInit = false;
            state.initMap = true;
            const context = state._deferredContext || getContext();
            const element = state._deferredElement || getElement();
            state._deferredContext = null;
            state._deferredElement = null;
            const accentColor = context.accentColor || '#3498db';
            const markerStyle = context.markerStyle || 'circle';
            // Si markerColor est défini et non vide, l'utiliser, sinon utiliser accentColor
            const markerColor = (context.markerColor && context.markerColor.trim() !== '') ? context.markerColor : accentColor;
            const markerHoverColor = context.markerHoverColor || '#e74c3c';

            // Libellé du lien popup (pour WPML/WPM). Stocké dans le state pour être accessible partout (AJAX inclus).
            state.moreInfoLabel = getMoreInfoLabel(context);
            
            // Stocker les couleurs dans le state pour les utiliser plus tard (disponible partout)
            state.markerColor = markerColor;
            state.markerStyle = markerStyle;
            state.accentColor = accentColor;
            
            const mapStyle = context.mapStyle || 'light';
            // Valeurs par défaut fixes pour simplifier
            const minZoom = 5;
            const maxZoom = 12;
            // Calibrage amélioré pour des transitions plus fluides
            const zoomSnap = 0.5; // Permet des demi-niveaux pour des transitions plus douces
            const zoomDelta = 0.5; // Incréments plus petits pour un contrôle plus fin
            const enableScrollWheelZoom = false;
            // Valider et contraindre le zoom initial entre minZoom et maxZoom (+ léger dézoom < 600px)
            const initialZoom = getResponsiveInitialZoom(context.initialZoom, minZoom, maxZoom);
            
            // Centre initial par défaut de la région Grand Est
            // Ajusté pour couvrir tous les départements du Grand Est
            const initialCenter = [48.7, 5.5];
            
            // Stocker le zoom initial et les limites dans le state pour pouvoir les surveiller
            state.currentInitialZoom = initialZoom;
            state.minZoom = minZoom;
            state.maxZoom = maxZoom;

            // Définir des bornes pour limiter l'affichage/pan à la France métropolitaine
            const franceBounds = L.latLngBounds([
                [41.0, -5.5],  // Sud-Ouest (près de Pyrénées/Atlantique)
                [51.5, 9.8]    // Nord-Est (près de Belgique/Allemagne/Suisse)
            ]);

            const map = L.map(context.mapId, {
                scrollWheelZoom: enableScrollWheelZoom,
                dragging: true, // Permettre de déplacer la carte en la grabant
                touchZoom: true, // Permettre le zoom tactile
                doubleClickZoom: true, // Permettre le zoom double-clic
                boxZoom: true, // Permettre le zoom par sélection
                keyboard: true, // Permettre la navigation au clavier
                gestureHandling: false, // Désactiver pour permettre le drag
                maxBounds: franceBounds,
                maxBoundsViscosity: 1.0,
                worldCopyJump: false,
                minZoom: minZoom,
                maxZoom: maxZoom,
                zoomSnap: zoomSnap, // Précision des niveaux de zoom (configurable depuis l'éditeur)
                zoomDelta: zoomDelta // Incrément de zoom pour les contrôles +/- (configurable depuis l'éditeur)
            }).setView(initialCenter, initialZoom);

            state.map = map;

            // Ajuster automatiquement le zoom initial quand on passe sous/au-dessus de 600px (ex: rotation mobile)
            if (!state._responsiveZoomListenerAdded && typeof window !== 'undefined') {
                state._responsiveZoomListenerAdded = true;
                let lastIsNarrow = window.innerWidth < 600;
                window.addEventListener('resize', () => {
                    const isNarrow = window.innerWidth < 600;
                    if (isNarrow !== lastIsNarrow) {
                        lastIsNarrow = isNarrow;
                        // Recalcule et applique le zoom initial responsive via le contexte
                        try {
                            store('gt/ctv').actions.syncZoomFromContext();
                        } catch (e) {
                            // no-op
                        }
                    }
                }, { passive: true });
            }
        

            L.tileLayer(getTileLayerUrl(mapStyle), {
                attribution: getTileLayerAttribution(mapStyle),
                subdomains: 'abcd',
                maxZoom: maxZoom, // Utiliser le même maxZoom que la carte
            }).addTo(map);

            // S'assurer que les contrôles de zoom sont dans le conteneur leaflet-top.leaflet-right
            // Leaflet les ajoute automatiquement, mais on s'assure que le conteneur a la bonne classe
            setTimeout(() => {
                const zoomControl = map._container.querySelector('.leaflet-control-zoom');
                if (zoomControl) {
                    const topContainer = zoomControl.closest('.leaflet-top');
                    if (topContainer && !topContainer.classList.contains('leaflet-right')) {
                        topContainer.classList.add('leaflet-right');
                        // Retirer leaflet-left si présent
                        topContainer.classList.remove('leaflet-left');
                    }
                }
            }, 100);

            // Ajouter les polygones des départements de la région Grand Est
            // Ajouter les polygones des départements de la région Grand Est (asynchrone)
            addGrandEstDepartments(map).catch(error => {
                console.error('Erreur lors de l\'ajout des départements:', error);
            });

            
            // Grouper les campings par département
            const campingsByDepartment = {};
            if (state.campings && Array.isArray(state.campings)) {
                state.campings.forEach(camping => {
                    const deptCode = getDepartmentCode(camping);
                    if (deptCode) {
                        if (!campingsByDepartment[deptCode]) {
                            campingsByDepartment[deptCode] = [];
                        }
                        campingsByDepartment[deptCode].push(camping);
                    }
                });
            }
            
            
            // Créer les marqueurs individuels mais les masquer au départ
            // Note: Les campings sans GPS seront chargés via AJAX plus tard
            if (state.campings && Array.isArray(state.campings)) {
                state.campings.forEach(camping => {
                // Vérifier que les coordonnées GPS existent et sont valides
                // Si null, le GPS sera chargé via AJAX
                if (!camping.gpsCoordinates || camping.gpsCoordinates.latitude === null || camping.gpsCoordinates.longitude === null) {
                    // GPS sera chargé via AJAX, ne pas créer le marqueur maintenant
                    return;
                }
                
                const { latitude, longitude } = camping.gpsCoordinates;
                
                // Vérifier que les coordonnées ne sont pas 0
                if (latitude === 0 || longitude === 0) {
                    // GPS à 0, sera peut-être chargé via AJAX
                    return;
                }

                const customIcon = L.divIcon({
                    html: createModernSVGMarker(markerColor, 20, markerStyle),
                    className: 'modern-svg-marker',
                    iconSize: [40, 40],
                    iconAnchor: getMarkerIconAnchor(markerStyle),
                });

                const marker = L.marker([latitude, longitude], { icon: customIcon });
                // Ne pas ajouter à la carte au départ, seulement créer le marqueur
                // Construire le contenu de la popup avec l'adresse et le lien
                let popupContent = '<div class="camping-popup-content">';
                // Nom du camping (en premier)
                if (camping.name) {
                    popupContent += `<div class="camping-popup-name"><b>${camping.name}</b></div>`;
                }
                // Adresse : rue sur la première ligne, code postal + ville sur la deuxième
                if (camping.address || camping.postalCode || camping.city) {
                    popupContent += '<div class="camping-popup-address">';
                    if (camping.address) {
                        popupContent += `<div class="camping-popup-address-line">${camping.address}</div>`;
                    }
                    if (camping.postalCode || camping.city) {
                        const postalCity = [camping.postalCode, camping.city].filter(Boolean).join(' ');
                        popupContent += `<div class="camping-popup-address-line">${postalCity}</div>`;
                    }
                    popupContent += '</div>';
                }
                // Lien vers la page camping (enfin)
                if (camping.url) {
                    popupContent += `<a href="${camping.url}" class="camping-popup-link">${state.moreInfoLabel}</a>`;
                }
                popupContent += '</div>';
                marker.bindPopup(popupContent);
                enablePopupOnHover(marker, state, camping.idSite);

                marker.on('click', () => {
                    state.selectedCamping = camping;
                    const queryElement = element.ref.parentNode;
                    resetActiveElements(queryElement);
                    activateCamping(queryElement, camping.idSite);

                    marker.openPopup();

                    setTimeout(() => adjustMapHeight(map, queryElement), 200);
                });

                // Ajouter les événements de hover sur les markers
                marker.on('mouseover', () => {
                    const queryElement = element.ref.parentNode;
                    const campingElement = queryElement.querySelector(`[data-gt-camping="${camping.idSite}"]`);
                    if (campingElement) {
                        campingElement.classList.add('hovered');
                    }
                });

                marker.on('mouseout', () => {
                    const queryElement = element.ref.parentNode;
                    const campingElement = queryElement.querySelector(`[data-gt-camping="${camping.idSite}"]`);
                    if (campingElement) {
                        campingElement.classList.remove('hovered');
                    }
                });

                marker.on('popupclose', () => {
                    if (state.selectedCamping?.idSite === camping.idSite) {
                        const queryElement = element.ref.parentNode;
                        resetActiveElements(queryElement);
                        state.selectedCamping = false;
                        resetMarkers(state, markerColor, markerStyle, map);
                    }
                });

                // IMPORTANT: Toujours utiliser String() pour la cohérence des clés
                const idSiteKey = String(camping.idSite);
                state.markers[idSiteKey] = marker;
                
                // Stocker le style original du marker
                state.markerStyles[idSiteKey] = {
                    color: markerColor,
                    style: markerStyle,
                    hoverColor: markerHoverColor
                };
                });
            }

            
            if (Object.keys(campingsByDepartment).length === 0) {
                console.warn('initMap: Aucun département trouvé avec des campings !');
                console.warn('initMap: state.campings.length =', state.campings ? state.campings.length : 0);
                if (state.campings && state.campings.length > 0) {
                    console.warn('initMap: Premier camping sample:', JSON.stringify(state.campings[0], null, 2));
                }
            }
            
            Object.keys(campingsByDepartment).forEach(deptCode => {
                const campings = campingsByDepartment[deptCode];
                const count = campings.length;
                const center = getDepartmentClusterCenter(campings, deptCode);
                
                
                if (!center || !center[0] || !center[1]) {
                    console.error('initMap: Centre invalide pour département', deptCode, ':', center);
                    return;
                }
                
                const clusterIcon = createDepartmentClusterIcon(count);
                const clusterMarker = L.marker(center, { icon: clusterIcon }).addTo(map);
                
                
                // Vérifier la présence de l'élément DOM sans forcer de recalcul de layout.
                setTimeout(() => {
                    const clusterEl = clusterMarker.getElement();
                    if (!clusterEl) {
                        console.warn(`initMap: Cluster ${deptCode} - élément DOM introuvable!`);
                    }
                }, 100);
                
                clusterMarker.on('click', () => {
                    store('gt/ctv').actions.zoomToDepartment(deptCode, campings);
                });
                
                state.departmentClusters[deptCode] = clusterMarker;
            });
            
            // Marquer que les clusters viennent d'être créés pour empêcher updateMap() de les masquer
            state.clustersJustCreated = true;
            setTimeout(() => {
                state.clustersJustCreated = false;
            }, 5000); // Protection pendant 5 secondes (augmenté de 2 à 5 secondes)
            
   
            // Vérifier chaque cluster individuellement
            Object.keys(state.departmentClusters).forEach(deptCode => {
                const cluster = state.departmentClusters[deptCode];
                const position = cluster.getLatLng();
                const isVisible = map.hasLayer(cluster);
                const inBounds = map.getBounds().contains(position);
            });

            // Écouter les changements de zoom pour basculer entre clusters et pins
            map.on('zoomend', () => {
                store('gt/ctv').actions.handleZoomChange();
            });

            // Charger les GPS via AJAX si les campings n'ont pas de coordonnées GPS
            if (state.campings && state.campings.length > 0) {
                const campingsWithoutGPS = state.campings.filter(c => 
                    !c.gpsCoordinates || 
                    c.gpsCoordinates.latitude === null || 
                    c.gpsCoordinates.longitude === null ||
                    c.gpsCoordinates.latitude === 0 ||
                    c.gpsCoordinates.longitude === 0
                );
                
                if (campingsWithoutGPS.length > 0) {
                    
                    // Récupérer les post IDs
                    const postIds = campingsWithoutGPS
                        .map(c => c.postId)
                        .filter(Boolean);
                    
                    if (postIds.length > 0) {
                        // Déterminer l'URL AJAX
                        const ajaxUrl = typeof ajaxurl !== 'undefined' ? ajaxurl : 
                                       (window.wp?.ajax?.url || '/wp-admin/admin-ajax.php');
                        
                        // Appeler l'endpoint AJAX pour récupérer les GPS
                        fetch(ajaxUrl, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/x-www-form-urlencoded',
                            },
                            body: new URLSearchParams({
                                action: 'gt_ws_ctv_get_campings_gps',
                                post_ids: JSON.stringify(postIds),
                            }),
                        })
                        .then(response => {
                            if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.status}`);
                            }
                            return response.text().then(text => {
                                try {
                                    return JSON.parse(text);
                                } catch (e) {
                                    console.error('initMap: Erreur de parsing JSON:', e, 'Texte:', text);
                                    throw e;
                                }
                            });
                        })
                        .then(data => {
                            console.log('initMap: Données AJAX parsées:', data);
                            if (data.success && data.data && Object.keys(data.data).length > 0) {
                                
                                // Mettre à jour les coordonnées GPS dans state.campings
                                Object.keys(data.data).forEach(postId => {
                                    const gpsData = data.data[postId];
                                    const camping = state.campings.find(c => c.postId == postId || c.idSite == gpsData.campingKey);
                                    if (camping && gpsData.latitude && gpsData.longitude) {
                                        camping.gpsCoordinates = {
                                            latitude: gpsData.latitude,
                                            longitude: gpsData.longitude,
                                        };
                                        
                                        // Créer le marqueur si la carte est déjà initialisée
                                        if (state.map && state.markers) {
                                            // En callback async, getContext() peut être indisponible hors scope interactivity.
                                            let context = {};
                                            try {
                                                context = getContext() || {};
                                            } catch (error) {
                                                context = {};
                                            }
                                            const currentMarkerColor = context.markerColor || (context.accentColor || '#3498db');
                                            const currentMarkerStyle = context.markerStyle || 'circle';
                                            
                                            const marker = L.marker([gpsData.latitude, gpsData.longitude], {
                                                icon: L.divIcon({
                                                    html: createModernSVGMarker(currentMarkerColor, 20, currentMarkerStyle),
                                                    className: 'modern-svg-marker',
                                                    iconSize: [40, 40],
                                                    iconAnchor: getMarkerIconAnchor(currentMarkerStyle),
                                                })
                                            });
                                            
                                            // Construire la popup
                                            let popupContent = '<div class="camping-popup-content">';
                                            if (camping.name) {
                                                popupContent += `<div class="camping-popup-name"><b>${camping.name}</b></div>`;
                                            }
                                            if (camping.address || camping.postalCode || camping.city) {
                                                popupContent += '<div class="camping-popup-address">';
                                                if (camping.address) {
                                                    popupContent += `<div class="camping-popup-address-line">${camping.address}</div>`;
                                                }
                                                if (camping.postalCode || camping.city) {
                                                    const postalCity = [camping.postalCode, camping.city].filter(Boolean).join(' ');
                                                    popupContent += `<div class="camping-popup-address-line">${postalCity}</div>`;
                                                }
                                                popupContent += '</div>';
                                            }
                                            if (camping.url) {
                                                popupContent += `<a href="${camping.url}" class="camping-popup-link">${state.moreInfoLabel}</a>`;
                                            }
                                            popupContent += '</div>';
                                            marker.bindPopup(popupContent);
                                            enablePopupOnHover(marker, state, camping.idSite);
                                            
                                            marker.on('click', () => {
                                                state.selectedCamping = camping;
                                                const queryElement = getElement().ref.parentNode;
                                                resetActiveElements(queryElement);
                                                activateCamping(queryElement, camping.idSite);
                                                marker.openPopup();
                                                setTimeout(() => adjustMapHeight(state.map, queryElement), 200);
                                            });
                                            
                                            // IMPORTANT: Toujours utiliser String() pour la cohérence des clés
                                            const idSiteKey = String(camping.idSite);
                                            state.markers[idSiteKey] = marker;
                                            
                                            // Stocker le style original
                                            state.markerStyles[idSiteKey] = {
                                                color: currentMarkerColor,
                                                style: currentMarkerStyle,
                                                hoverColor: context.markerHoverColor || '#e74c3c'
                                            };
                                        }
                                    }
                                });
                                
                                // Mettre à jour les clusters existants avec les nouveaux GPS chargés
                                // NE PAS supprimer les clusters existants, juste mettre à jour les nombres
                                const campingsByDepartment = {};
                                state.campings.forEach(camping => {
                                    if (camping.gpsCoordinates && 
                                        camping.gpsCoordinates.latitude && 
                                        camping.gpsCoordinates.longitude &&
                                        camping.gpsCoordinates.latitude !== 0 &&
                                        camping.gpsCoordinates.longitude !== 0) {
                                        const deptCode = getDepartmentCode(camping);
                                        if (deptCode) {
                                            if (!campingsByDepartment[deptCode]) {
                                                campingsByDepartment[deptCode] = [];
                                            }
                                            campingsByDepartment[deptCode].push(camping);
                                        }
                                    }
                                });
                                
                                // Mettre à jour les clusters existants au lieu de les recréer
                                Object.keys(state.departmentClusters || {}).forEach(deptCode => {
                                    const cluster = state.departmentClusters[deptCode];
                                    const campings = campingsByDepartment[deptCode] || [];
                                    const count = campings.length;
                                    
                                    if (count > 0) {
                                        // Mettre à jour l'icône avec le nouveau nombre
                                        const clusterIcon = createDepartmentClusterIcon(count);
                                        cluster.setIcon(clusterIcon);
                                        
                                        // Mettre à jour le gestionnaire de clic
                                        cluster.off('click');
                                        cluster.on('click', () => {
                                            store('gt/ctv').actions.zoomToDepartment(deptCode, campings);
                                        });
                                        
                                        // S'assurer que le cluster est toujours sur la carte
                                        if (!state.map.hasLayer(cluster)) {
                                            cluster.addTo(state.map);
                                        }
                                    }
                                    // Si count === 0, garder le cluster tel quel (il sera mis à jour plus tard)
                                });
                                
                                console.log('initMap: Clusters mis à jour avec GPS chargés:', Object.keys(state.departmentClusters || {}).length, 'clusters existants');
                                
                                // Vérifier que les clusters sont bien sur la carte
                                Object.keys(state.departmentClusters || {}).forEach(deptCode => {
                                    const cluster = state.departmentClusters[deptCode];
                                    const isVisible = state.map.hasLayer(cluster);
                                    console.log(`initMap: Cluster ${deptCode} visible sur la carte?`, isVisible);
                                });
                            } else if (data.success && data.data && Object.keys(data.data).length === 0) {
                                // Aucun GPS chargé, mais ce n'est pas une erreur
                                // Garder les clusters existants tels quels
                                console.log('initMap: Nombre de clusters conservés:', Object.keys(state.departmentClusters || {}).length);
                            } else {
                                console.warn('initMap: Échec du chargement des GPS via AJAX:', data);
                                console.warn('initMap: Réponse complète:', JSON.stringify(data, null, 2));
                            }
                        })
                        .catch(error => {
                            console.error('initMap: Erreur lors du chargement des GPS:', error);
                            console.error('initMap: Stack trace:', error.stack);
                        });
                    }
                }
            }
            
            // Initialiser l'affichage selon le zoom initial
            // Ne pas appeler updateMap() au chargement initial car les clusters sont déjà créés et ajoutés
            // updateMap() sera appelé uniquement si un filtre région est présent dans l'URL
            // Les clusters créés ci-dessus restent visibles par défaut
            // DÉSACTIVÉ TEMPORAIREMENT pour éviter que handleZoomChange masque les clusters
            // setTimeout(() => {
            //     store('gt/ctv').actions.handleZoomChange();
            // }, 100);

            // Ajuster le centre initial pour mieux centrer sur les clusters (sans changer le zoom)
            setTimeout(() => {
                try {
                    if (!state.map || !state.departmentClusters) {
                        return;
                    }
                    
                    const clusterPositions = Object.values(state.departmentClusters).map(cluster => {
                        try {
                            if (cluster && typeof cluster.getLatLng === 'function') {
                                return cluster.getLatLng();
                            }
                            return null;
                        } catch (e) {
                            return null;
                        }
                    }).filter(pos => pos !== null);
                    
                    if (clusterPositions.length > 0) {
                        const bounds = L.latLngBounds(clusterPositions);
                        const optimalCenterLatLng = bounds.getCenter();
                        const optimalCenter = [optimalCenterLatLng.lat, optimalCenterLatLng.lng];
                        const currentZoom = state.map.getZoom();
                        
                        // Ajuster uniquement le centre, en gardant le zoom initial
                        state.map.setView(optimalCenter, currentZoom, { animate: false });
                    }
                } catch (error) {
                    console.warn('Erreur lors de l\'ajustement du centre de la carte:', error);
                }
            }, 300);

            // Utiliser le zoom initial exactement comme défini, sans ajustement automatique
            // Le zoom initial est déjà appliqué via setView() à la ligne 250
            // Cela garantit que le zoom reste exactement à la valeur définie dans l'éditeur
            
            // Appliquer un filtre région initial selon l'URL
            try {
                const normalize = (str) => (str || '')
                    .toString()
                    .trim()
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/\p{Diacritic}/gu, '');

                const url = new URL(window.location.href);
                const urlRegion = url.searchParams.get('region') || '';
                const selectedRegion = urlRegion.toString().trim();
                const normalizedSelected = normalize(selectedRegion);

                if (selectedRegion) {
                    const matchingIds = (state.campings || [])
                        .filter(c => {
                            const campingRegion = (c.region || '').toString().trim();
                            return normalize(campingRegion) === normalizedSelected || campingRegion === selectedRegion;
                        })
                        .map(c => c.idSite?.toString())
                        .filter(Boolean);
                    state.visibleCampingsIds = matchingIds;
                    if (store('gt/ctv')?.actions?.updateMap) {
                        store('gt/ctv').actions.updateMap();
                    }
                } else {
                    // Ne pas écraser visibleCampingsIds s'il n'y a pas de filtre région
                    // Laisser la valeur initiale depuis PHP qui contient tous les IDs
                    // state.visibleCampingsIds reste inchangé
                }
            } catch (error) {
                console.error('Erreur filtre région initial:', error);
            }
            
            
        },
        updateMap: () => {
            
            if(!state.map)return;
            
            // PROTECTION: Ne pas masquer les clusters s'ils viennent d'être créés
            if (state.clustersJustCreated) {
                return; // Retourner immédiatement pour éviter toute modification
            }
            
            // PROTECTION: Si un département est sélectionné, ne pas modifier les clusters
            // zoomToDepartment gère déjà l'affichage des pins/clusters du département
            if (state.currentDepartment) {
                console.log('updateMap: Département sélectionné (', state.currentDepartment, '), RETOUR IMMÉDIAT - zoomToDepartment gère l\'affichage');
                return; // Retourner immédiatement pour éviter de modifier les clusters
            }

            // Si visibleCampingsIds n'est pas encore initialisé ou est vide (pas de filtres), initialiser avec tous les IDs
            if (state.visibleCampingsIds === null || state.visibleCampingsIds === undefined || (Array.isArray(state.visibleCampingsIds) && state.visibleCampingsIds.length === 0 && !state.showSearchResults)) {
                console.log('updateMap: visibleCampingsIds non initialisé ou vide (pas de filtres), initialisation avec tous les IDs');
                if (state.campings && Array.isArray(state.campings)) {
                    state.visibleCampingsIds = state.campings
                        .map(c => c.idSite?.toString())
                        .filter(Boolean);
                } else {
                    state.visibleCampingsIds = [];
                }
            }

            // Distinguer deux cas :
            // 1. Recherche par nom (showSearchResults === true) : masquer les clusters, afficher directement les marqueurs
            // 2. Filtres de la sidebar (showSearchResults === false ou undefined) : garder les clusters, mettre à jour avec les campings filtrés
            // Par défaut, si showSearchResults n'est pas défini, considérer que ce n'est pas une recherche
            const isSearchActive = state.showSearchResults === true;
            
            // Vérifier si visibleCampingsIds contient tous les IDs (pas de filtres) ou seulement une partie (filtres actifs)
            const totalCampings = state.campings ? state.campings.length : 0;
            const visibleCount = state.visibleCampingsIds ? state.visibleCampingsIds.length : 0;
            const hasFilters = totalCampings > 0 && visibleCount > 0 && visibleCount < totalCampings;
   
            
            // Si une recherche est active, toujours traiter même si hasFilters est false
            // Si visibleCampingsIds contient des IDs ET qu'il y a des filtres actifs OU une recherche active
            if (state.visibleCampingsIds && state.visibleCampingsIds.length > 0 && (hasFilters || isSearchActive)) {
                if (isSearchActive) {
                    
                   
                    
                    // Masquer tous les clusters de départements
                    if (state.departmentClusters) {
                        Object.values(state.departmentClusters).forEach(cluster => {
                            if (state.map.hasLayer(cluster)) {
                                state.map.removeLayer(cluster);
                            }
                        });
                    }
                    
                    // Masquer tous les clusters de pins
                    if (state.pinClusters) {
                        Object.values(state.pinClusters).forEach(cluster => {
                            if (state.map.hasLayer(cluster)) {
                                state.map.removeLayer(cluster);
                            }
                        });
                        state.pinClusters = {};
                    }
                    
                    // Réinitialiser le département sélectionné pour permettre l'affichage direct des marqueurs
                    state.currentDepartment = null;
                    state.departmentZoomLevel = null;
                    state.pinClusterZoomLevel = null;
                    
                    // Convertir visibleCampingsIds en Set pour une recherche rapide
                    const visibleIdsSet = new Set((state.visibleCampingsIds || []).map(id => String(id)));
                    
                    // IMPORTANT: S'assurer que les marqueurs sont créés avant de les afficher
                    // Créer les marqueurs manquants pour les campings recherchés
                    let context = null;
                    try {
                        context = getContext();
                    } catch (e) {
                        console.warn('updateMap: getContext() non disponible, utilisation des valeurs par défaut');
                    }
                    const markerColor = (context && context.markerColor) || (context && context.accentColor) || '#3498db';
                    const markerStyle = (context && context.markerStyle) || 'circle';
                    
                    // Fonction pour charger les GPS manquants via AJAX
                    const loadMissingGPS = (campingIds) => {
                        if (!campingIds || campingIds.length === 0) return Promise.resolve();
                        
                        // Récupérer les postIds des campings qui n'ont pas de GPS
                        const campingsWithoutGPS = state.campings.filter(c => {
                            const idStr = String(c.idSite);
                            return campingIds.includes(idStr) && (!c.gpsCoordinates || !c.gpsCoordinates.latitude || !c.gpsCoordinates.longitude) && c.postId;
                        });
                        
                        if (campingsWithoutGPS.length === 0) {
                            return Promise.resolve();
                        }
                        
                        const postIds = campingsWithoutGPS.map(c => c.postId).filter(Boolean);
                        
                        // Déterminer l'URL AJAX
                        const ajaxUrl = typeof ajaxurl !== 'undefined' ? ajaxurl : 
                                       (window.wp?.ajax?.url || '/wp-admin/admin-ajax.php');
                        
                        return fetch(ajaxUrl, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/x-www-form-urlencoded',
                            },
                            body: new URLSearchParams({
                                action: 'gt_ws_ctv_get_campings_gps',
                                post_ids: JSON.stringify(postIds),
                            }),
                        })
                            .then(response => response.json())
                            .then(data => {
                                if (data.success && data.data) {
                                    Object.keys(data.data).forEach(postId => {
                                        const gpsData = data.data[postId];
                                        const camping = state.campings.find(c => c.postId == postId || c.idSite == gpsData.campingKey);
                                        if (camping && gpsData.latitude && gpsData.longitude) {
                                            camping.gpsCoordinates = {
                                                latitude: gpsData.latitude,
                                                longitude: gpsData.longitude,
                                            };
                                            
                                            // Créer le marqueur maintenant que les GPS sont disponibles
                                            const idSiteStr = String(camping.idSite);
                                            if (!state.markers[idSiteStr] && state.map) {
                                                const marker = L.marker([gpsData.latitude, gpsData.longitude], {
                                                    icon: L.divIcon({
                                                        html: createModernSVGMarker(markerColor, 20, markerStyle),
                                                        className: 'modern-svg-marker',
                                                        iconSize: [40, 40],
                                                        iconAnchor: getMarkerIconAnchor(markerStyle),
                                                    })
                                                });
                                                
                                                // Construire la popup
                                                let popupContent = '<div class="camping-popup-content">';
                                                if (camping.name) {
                                                    popupContent += `<div class="camping-popup-name"><b>${camping.name}</b></div>`;
                                                }
                                                if (camping.address || camping.postalCode || camping.city) {
                                                    popupContent += '<div class="camping-popup-address">';
                                                    if (camping.address) {
                                                        popupContent += `<div class="camping-popup-address-line">${camping.address}</div>`;
                                                    }
                                                    if (camping.postalCode || camping.city) {
                                                        const postalCity = [camping.postalCode, camping.city].filter(Boolean).join(' ');
                                                        popupContent += `<div class="camping-popup-address-line">${postalCity}</div>`;
                                                    }
                                                    popupContent += '</div>';
                                                }
                                                if (camping.url) {
                                                    popupContent += `<a href="${camping.url}" class="camping-popup-link">${state.moreInfoLabel}</a>`;
                                                }
                                                popupContent += '</div>';
                                                marker.bindPopup(popupContent);
                                                enablePopupOnHover(marker, state, camping.idSite);
                                                
                                                state.markers[idSiteStr] = marker;
                                                state.markerStyles[idSiteStr] = { 
                                                    color: markerColor, 
                                                    style: markerStyle, 
                                                    hoverColor: (context && context.markerHoverColor) || '#e74c3c' 
                                                };
                                            }
                                        }
                                    });
                                    // Relancer updateMap après avoir chargé les GPS
                                    setTimeout(() => {
                                        store('gt/ctv').actions.updateMap();
                                    }, 100);
                                }
                            })
                            .catch(error => {
                                console.error('updateMap: Erreur lors du chargement GPS:', error);
                            });
                    };
                    
                    if (state.visibleCampingsIds && Array.isArray(state.visibleCampingsIds) && state.visibleCampingsIds.length > 0) {
                        
                        // Vérifier d'abord si des GPS manquent et les charger
                        const campingsWithoutGPS = state.visibleCampingsIds.filter(idSite => {
                            const idSiteStr = String(idSite);
                            const camping = state.campings.find(c => String(c.idSite) === idSiteStr);
                            return camping && (!camping.gpsCoordinates || !camping.gpsCoordinates.latitude || !camping.gpsCoordinates.longitude);
                        });
                        
                        if (campingsWithoutGPS.length > 0) {
                            loadMissingGPS(state.visibleCampingsIds.map(String));
                            // Continuer quand même pour créer les marqueurs disponibles
                        }
                        
                        state.visibleCampingsIds.forEach(idSite => {
                            const idSiteStr = String(idSite);
                            const idSiteNum = Number(idSite);
                            
                            // Vérifier si le marqueur existe déjà (essayer plusieurs formats)
                            let existingMarker = state.markers[idSiteStr] || state.markers[idSiteNum] || state.markers[idSite];
                            
                            // Si le marqueur n'existe pas encore, le créer
                            if (!existingMarker && state.campings) {
                                const camping = state.campings.find(c => String(c.idSite) === idSiteStr || Number(c.idSite) === idSiteNum || c.idSite === idSite);
                                if (camping && camping.gpsCoordinates && camping.gpsCoordinates.latitude && camping.gpsCoordinates.longitude) {
                                    const { latitude, longitude } = camping.gpsCoordinates;
                                    const customIcon = L.divIcon({
                                        html: createModernSVGMarker(markerColor, 20, markerStyle),
                                        className: 'modern-svg-marker',
                                        iconSize: [40, 40],
                    iconAnchor: getMarkerIconAnchor(markerStyle),
                                    });
                                    const marker = L.marker([latitude, longitude], { icon: customIcon });
                                    
                                    // Construire le contenu de la popup
                                    let popupContent = '<div class="camping-popup-content">';
                                    if (camping.name) {
                                        popupContent += `<div class="camping-popup-name"><b>${camping.name}</b></div>`;
                                    }
                                    if (camping.address || camping.postalCode || camping.city) {
                                        popupContent += '<div class="camping-popup-address">';
                                        if (camping.address) {
                                            popupContent += `<div class="camping-popup-address-line">${camping.address}</div>`;
                                        }
                                        if (camping.postalCode || camping.city) {
                                            const postalCity = [camping.postalCode, camping.city].filter(Boolean).join(' ');
                                            popupContent += `<div class="camping-popup-address-line">${postalCity}</div>`;
                                        }
                                        popupContent += '</div>';
                                    }
                                    if (camping.url) {
                                        popupContent += `<a href="${camping.url}" class="camping-popup-link">${state.moreInfoLabel}</a>`;
                                    }
                                    popupContent += '</div>';
                                    marker.bindPopup(popupContent);
                                    enablePopupOnHover(marker, state, camping.idSite);
                                    
                                    // Stocker le marqueur avec toutes les clés possibles pour faciliter la recherche
                                    state.markers[idSiteStr] = marker;
                                    // Aussi stocker avec la clé numérique si différente
                                    if (idSiteNum.toString() !== idSiteStr) {
                                        state.markers[idSiteNum] = marker;
                                    }
                                    state.markerStyles[idSiteStr] = { 
                                        color: markerColor, 
                                        style: markerStyle, 
                                        hoverColor: context.markerHoverColor || '#e74c3c' 
                                    };
                                } else {
                                    console.warn('updateMap: Camping trouvé mais sans GPS valide pour ID:', idSiteStr);
                                }
                            } else {
                                console.log('updateMap: Marqueur existe déjà pour ID:', idSiteStr);
                            }
                        });
                    }
                    
                    Object.keys(state.markers).forEach(idSite => {
                        const marker = state.markers[idSite];
                        if (marker && state.map && state.map.hasLayer(marker)) {
                            state.map.removeLayer(marker);
                        }
                    });
                    
                    // Afficher uniquement les marqueurs correspondant aux campings recherchés
                    let visibleMarkersCount = 0;
                    const markersNotFound = [];
                    
                    if (state.visibleCampingsIds && Array.isArray(state.visibleCampingsIds) && state.visibleCampingsIds.length > 0) {
                        
                        state.visibleCampingsIds.forEach(idSite => {
                            // Essayer les deux formats (string et nombre) pour trouver le marqueur
                            const idSiteStr = String(idSite);
                            const idSiteNum = Number(idSite);
                            let marker = state.markers[idSiteStr] || state.markers[idSiteNum] || state.markers[idSite];
                            
                            // Si toujours pas trouvé, chercher dans toutes les clés avec une comparaison souple
                            if (!marker) {
                                Object.keys(state.markers).forEach(key => {
                                    if (String(key) === idSiteStr || Number(key) === idSiteNum || key === idSite) {
                                        marker = state.markers[key];
                                    }
                                });
                            }
                            
                            if (marker) {
                                // Vérifier que le marqueur est valide
                                if (marker && marker.addTo && state.map) {
                                    if (!state.map.hasLayer(marker)) {
                                        marker.addTo(state.map);
                                        visibleMarkersCount++;
                                    } else {
                                        visibleMarkersCount++;
                                    }
                                } else {
                                    console.warn('updateMap: Marqueur invalide pour ID:', idSiteStr, 'marker:', marker, 'map:', !!state.map);
                                    markersNotFound.push(idSiteStr);
                                }
                            } else {
                                markersNotFound.push(idSiteStr);
                                console.warn('updateMap: ✗ Marqueur non trouvé pour ID:', idSiteStr, '(essayé:', idSiteStr, idSiteNum, idSite, ')');
                                console.warn('updateMap: Clés disponibles:', Object.keys(state.markers).slice(0, 10), '...');
                            }
                        });
                    } else {
                        console.warn('updateMap: visibleCampingsIds est vide ou invalide');
                    }
                    
                    if (markersNotFound.length > 0) {
                        console.warn('updateMap: Marqueurs non trouvés pour les IDs:', markersNotFound);
                    }
                    
                    // IMPORTANT: Retourner ici pour éviter que le code suivant ne masque les marqueurs
                    return;
                } else {
                    // CAS 2: Filtres de la sidebar - garder les clusters et les mettre à jour
                    // Convertir visibleCampingsIds en Set pour une recherche rapide
                    const visibleIdsSet = new Set((state.visibleCampingsIds || []).map(id => String(id)));
                
                    
                    // Mettre à jour les clusters de départements avec le nombre de campings filtrés
                    // IMPORTANT: Toujours mettre à jour les clusters, même si un département est sélectionné
                    // car l'utilisateur peut revenir au niveau des départements
                    if (state.departmentClusters) {
                        
                        Object.keys(state.departmentClusters).forEach(deptCode => {
                            const cluster = state.departmentClusters[deptCode];
                            // Compter les campings filtrés de ce département
                            const filteredCampings = state.campings.filter(c => {
                                const deptCodeCamping = getDepartmentCode(c);
                                const matchesDepartment = deptCodeCamping === deptCode;
                                const matchesFilter = visibleIdsSet.has(String(c.idSite));
                                return matchesDepartment && matchesFilter;
                            });
                            const filteredCount = filteredCampings.length;
                            
                            
                            if (filteredCount > 0) {
                                // Mettre à jour l'icône du cluster avec le nouveau nombre
                                const clusterIcon = createDepartmentClusterIcon(filteredCount);
                                cluster.setIcon(clusterIcon);
                                
                                // Mettre à jour le gestionnaire de clic pour utiliser les campings filtrés
                                cluster.off('click');
                                cluster.on('click', () => {
                                    store('gt/ctv').actions.zoomToDepartment(deptCode, filteredCampings);
                                });
                                
                                // Si aucun département n'est sélectionné, afficher le cluster
                                // Si un département est sélectionné, le cluster reste masqué (géré par zoomToDepartment)
                                if (!state.currentDepartment) {
                                    // Toujours ajouter le cluster à la carte s'il n'y est pas déjà
                                    if (!state.map.hasLayer(cluster)) {
                                        cluster.addTo(state.map);
                                    } else {
                                        console.log('updateMap: Cluster de département', deptCode, 'déjà sur la carte, mis à jour avec', filteredCount, 'campings');
                                    }
                                } else {
                                    // Département sélectionné, masquer le cluster
                                    if (state.map.hasLayer(cluster)) {
                                        state.map.removeLayer(cluster);
                                    }
                                }
                            } else {
                                // PROTECTION: Ne pas masquer le cluster s'il vient d'être créé
                                if (state.clustersJustCreated) {
                                    // S'assurer que le cluster est toujours affiché
                                    if (!state.map.hasLayer(cluster)) {
                                        cluster.addTo(state.map);
                                    }
                                } else {
                                    // Aucun camping filtré dans ce département, masquer le cluster
                                    // IMPORTANT: Toujours masquer, même si le cluster était déjà sur la carte
                                    if (state.map.hasLayer(cluster)) {
                                        state.map.removeLayer(cluster);
                                    }
                                    // Vérification supplémentaire après un court délai pour s'assurer que le cluster est bien retiré
                                    setTimeout(() => {
                                        if (state.map && state.map.hasLayer(cluster)) {
                                            state.map.removeLayer(cluster);
                                        }
                                    }, 100);
                                    // Vérification supplémentaire : s'assurer que le cluster est bien retiré
                                    // Parfois removeLayer ne fonctionne pas immédiatement, donc on vérifie après un court délai
                                    setTimeout(() => {
                                        if (state.map.hasLayer(cluster)) {
                                            state.map.removeLayer(cluster);
                                        }
                                    }, 100);
                                }
                            }
                        });
                    } else {
                        console.warn('updateMap: state.departmentClusters est vide');
                    }
                    
                    // IMPORTANT: Masquer tous les marqueurs individuels au niveau des clusters de départements
                    // quand des filtres sidebar sont actifs (pas de département sélectionné)
                    if (!state.currentDepartment) {
                        // Masquer tous les marqueurs individuels
                        Object.keys(state.markers).forEach(idSite => {
                            const marker = state.markers[idSite];
                            if (state.map.hasLayer(marker)) {
                                state.map.removeLayer(marker);
                            }
                        });
                        
                        // Vérification finale : s'assurer que seuls les clusters avec des campings filtrés sont affichés
                        if (state.departmentClusters) {
                            Object.keys(state.departmentClusters).forEach(deptCode => {
                                const cluster = state.departmentClusters[deptCode];
                                const filteredCampings = state.campings.filter(c => {
                                    const deptCodeCamping = getDepartmentCode(c);
                                    return deptCodeCamping === deptCode && visibleIdsSet.has(String(c.idSite));
                                });
                                
                                if (filteredCampings.length === 0) {
                                    // Aucun camping filtré, s'assurer que le cluster est masqué
                                    if (state.map.hasLayer(cluster)) {
                                        state.map.removeLayer(cluster);
                                    }
                                }
                            });
                        }
                    }
                    
                    // Mettre à jour les clusters de pins si un département est sélectionné
                    // IMPORTANT: Conserver les clusters de pins existants et les mettre à jour avec les filtres
                    if (state.currentDepartment) {
                        // Filtrer les campings du département selon visibleCampingsIds
                        let deptCampings = state.campings.filter(c => getDepartmentCode(c) === state.currentDepartment);
                        deptCampings = deptCampings.filter(c => visibleIdsSet.has(String(c.idSite)));
                        
                        const campingsWithMarkers = deptCampings.filter(c => {
                            return state.markers[c.idSite] && c.gpsCoordinates && c.gpsCoordinates.latitude && c.gpsCoordinates.longitude;
                        });
                        
                        if (campingsWithMarkers.length > 0) {
                            // Recréer les clusters de pins avec les campings filtrés
                            // Masquer d'abord les clusters existants
                            if (state.pinClusters) {
                                Object.values(state.pinClusters).forEach(cluster => {
                                    if (state.map.hasLayer(cluster)) {
                                        state.map.removeLayer(cluster);
                                    }
                                });
                            }
                            state.pinClusters = {};
                            
                            // Masquer aussi les pins individuels pour les recréer proprement
                            Object.keys(state.markers).forEach(idSite => {
                                const marker = state.markers[idSite];
                                const camping = state.campings.find(c => String(c.idSite) === String(idSite));
                                if (camping && getDepartmentCode(camping) === state.currentDepartment) {
                                    if (state.map.hasLayer(marker)) {
                                        state.map.removeLayer(marker);
                                    }
                                }
                            });
                            
                            // Recréer les clusters avec les campings filtrés
                            const context = getContext();
                            const markerColor = context.markerColor || (context.accentColor || '#3498db');
                            const clusters = clusterPins(campingsWithMarkers, state.markers, 2000);
                            
                            clusters.forEach((cluster, index) => {
                                if (cluster.campings.length > 1) {
                                    const clusterIcon = createPinClusterIcon(cluster.campings.length, markerColor);
                                    const clusterMarker = L.marker(cluster.center, { icon: clusterIcon }).addTo(state.map);
                                    
                                    clusterMarker.on('click', () => {
                                        store('gt/ctv').actions.zoomToPinCluster(cluster.campings);
                                    });
                                    
                                    const clusterId = `pin-cluster-${state.currentDepartment}-${index}`;
                                    state.pinClusters[clusterId] = clusterMarker;
                                } else {
                                    const camping = cluster.campings[0];
                                    const marker = state.markers[camping.idSite];
                                    if (marker && !state.map.hasLayer(marker)) {
                                        marker.addTo(state.map);
                                    }
                                }
                            });
                            
                        } else {
                            // Aucun camping filtré dans ce département, masquer les clusters de pins
                            if (state.pinClusters) {
                                Object.values(state.pinClusters).forEach(cluster => {
                                    if (state.map.hasLayer(cluster)) {
                                        state.map.removeLayer(cluster);
                                    }
                                });
                            }
                            state.pinClusters = {};
                        }
                    } else {
                        // Pas de département sélectionné, masquer les clusters de pins
                        if (state.pinClusters) {
                            Object.values(state.pinClusters).forEach(cluster => {
                                if (state.map.hasLayer(cluster)) {
                                    state.map.removeLayer(cluster);
                                }
                            });
                            state.pinClusters = {};
                        }
                    }
                }
            } else {
                
                // PROTECTION: Si les clusters viennent d'être créés, ne rien faire
                if (state.clustersJustCreated) {
                    console.log('updateMap: PROTECTION - Clusters viennent d\'être créés, ne pas les modifier (pas de filtres)');
                    return;
                }
                
                if (!state.currentDepartment) {
                    // Masquer tous les pins individuels d'abord
                    Object.keys(state.markers).forEach(idSite => {
                        const marker = state.markers[idSite];
                        if (state.map.hasLayer(marker)) {
                            state.map.removeLayer(marker);
                        }
                    });
                    
                    // Masquer tous les clusters de pins
                    if (state.pinClusters) {
                        Object.values(state.pinClusters).forEach(cluster => {
                            if (state.map.hasLayer(cluster)) {
                                state.map.removeLayer(cluster);
                            }
                        });
                        state.pinClusters = {};
                    }
                    
                    // Réafficher les clusters de départements avec le nombre total de campings
                    if (state.departmentClusters) {
                        
                        Object.keys(state.departmentClusters).forEach(deptCode => {
                            const cluster = state.departmentClusters[deptCode];
                            // Compter tous les campings du département (pas de filtres)
                            const allCampings = state.campings.filter(c => getDepartmentCode(c) === deptCode);
                            const totalCount = allCampings.length;
                            
                            
                            if (totalCount > 0) {
                                // Créer l'icône avec le nombre total
                                const clusterIcon = createDepartmentClusterIcon(totalCount);
                                // Mettre à jour l'icône avec le nombre total
                                cluster.setIcon(clusterIcon);
                                
                                // Mettre à jour le gestionnaire de clic pour utiliser tous les campings
                                cluster.off('click');
                                cluster.on('click', () => {
                                    store('gt/ctv').actions.zoomToDepartment(deptCode, allCampings);
                                });
                                
                                // S'assurer que le cluster est affiché
                                if (!state.map.hasLayer(cluster)) {
                                    cluster.addTo(state.map);
                                } else {
                                    console.log('updateMap: Cluster de département', deptCode, 'déjà sur la carte, mis à jour avec', totalCount, 'campings');
                                }
                            } else {
                                // PROTECTION: Ne pas masquer le cluster s'il vient d'être créé
                                if (state.clustersJustCreated) {
                                    // S'assurer que le cluster est toujours affiché
                                    if (!state.map.hasLayer(cluster)) {
                                        cluster.addTo(state.map);
                                    }
                                } else {
                                    // S'assurer que le cluster est toujours affiché
                                    if (!state.map.hasLayer(cluster)) {
                                        cluster.addTo(state.map);
                                    }
                                }
                            }
                        });
                    } else {
                        console.warn('updateMap: state.departmentClusters est vide ou non défini');
                    }
                    
                    // Réinitialiser le département sélectionné
                    state.currentDepartment = null;
                    state.departmentZoomLevel = null;
                    state.pinClusterZoomLevel = null;
                } else {
                    console.log('updateMap: Département sélectionné (', state.currentDepartment, '), ne pas modifier les clusters');
                }
                // Si currentDepartment est défini, ne rien faire - zoomToDepartment gère l'affichage
            }

            // 1) Synchroniser les marqueurs avec la source de vérité (state.campings)
            try {
                const context = getContext();
                const markerStyle = context.markerStyle || 'circle';
                const markerColor = context.markerColor || (context.accentColor || '#3498db');
                const currentIds = new Set((state.campings || []).map(c => String(c.idSite)));

                // a) Supprimer les marqueurs orphelins qui ne sont plus dans state.campings
                Object.keys(state.markers).forEach(idSite => {
                    if (!currentIds.has(String(idSite))) {
                        const marker = state.markers[idSite];
                        if (marker) {
                            try { state.map.removeLayer(marker); } catch (e) {}
                        }
                        delete state.markers[idSite];
                        delete state.markerStyles[idSite];
                    }
                });

                // b) Créer les marqueurs manquants présents dans state.campings (sans les ajouter à la carte)
                (state.campings || []).forEach(camping => {
                    const idSite = String(camping.idSite);
                    if (!state.markers[idSite] && camping.gpsCoordinates) {
                        const { latitude, longitude } = camping.gpsCoordinates;
                        if (latitude && longitude) {
                            const customIcon = L.divIcon({
                                html: createModernSVGMarker(markerColor, 20, markerStyle),
                                className: 'modern-svg-marker',
                                iconSize: [40, 40],
                                        iconAnchor: getMarkerIconAnchor(markerStyle),
                            });
                            const marker = L.marker([latitude, longitude], { icon: customIcon });
                            // Ne pas ajouter à la carte au départ - sera ajouté uniquement lors du zoom sur un département
                            // Construire le contenu de la popup avec l'adresse et le lien
                            let popupContent = '<div class="camping-popup-content">';
                            // Nom du camping (en premier)
                            if (camping.name) {
                                popupContent += `<div class="camping-popup-name"><b>${camping.name}</b></div>`;
                            }
                            // Adresse : rue sur la première ligne, code postal + ville sur la deuxième
                            if (camping.address || camping.postalCode || camping.city) {
                                popupContent += '<div class="camping-popup-address">';
                                if (camping.address) {
                                    popupContent += `<div class="camping-popup-address-line">${camping.address}</div>`;
                                }
                                if (camping.postalCode || camping.city) {
                                    const postalCity = [camping.postalCode, camping.city].filter(Boolean).join(' ');
                                    popupContent += `<div class="camping-popup-address-line">${postalCity}</div>`;
                                }
                                popupContent += '</div>';
                            }
                            // Lien vers la page camping (enfin)
                            if (camping.url) {
                                                popupContent += `<a href="${camping.url}" class="camping-popup-link">${state.moreInfoLabel}</a>`;
                            }
                            popupContent += '</div>';
                            marker.bindPopup(popupContent);
                            enablePopupOnHover(marker, state, camping.idSite);
                            state.markers[idSite] = marker;
                            state.markerStyles[idSite] = { color: markerColor, style: markerStyle, hoverColor: context.markerHoverColor || '#e74c3c' };
                        }
                    }
                });
            } catch (e) {
                // fail-safe
            }

            // 2) Appliquer la visibilité selon visibleCampingsIds
            // IMPORTANT: Si une recherche est active, afficher directement les marqueurs individuels
            // Si des filtres sidebar sont actifs, les clusters sont déjà gérés plus haut
            if (isSearchActive && state.visibleCampingsIds.length > 0 && !state.currentDepartment) {
                // CAS RECHERCHE: Afficher directement les marqueurs individuels (pas de clusters)
                const visibleIdsSet = new Set((state.visibleCampingsIds || []).map(id => String(id)));
                
                Object.keys(state.markers).forEach(idSite => {
                    const marker = state.markers[idSite];
                    if (visibleIdsSet.has(String(idSite))) {
                        if (!state.map.hasLayer(marker)) {
                            marker.addTo(state.map);
                        }
                    } else {
                        if (state.map.hasLayer(marker)) {
                            state.map.removeLayer(marker);
                        }
                    }
                });
            } else if (state.currentDepartment) {
                // Si un département est sélectionné, filtrer les marqueurs selon visibleCampingsIds
                // Les clusters de pins sont déjà gérés plus haut, mais on doit aussi filtrer les pins individuels
                const visibleIdsSet = new Set((state.visibleCampingsIds || []).map(id => String(id)));
                const hasPinClusters = Object.keys(state.pinClusters).length > 0;
                
                if (!hasPinClusters) {
                    // Pas de clusters, afficher les pins normalement mais filtrer selon visibleCampingsIds
                    Object.keys(state.markers).forEach(idSite => {
                        const marker = state.markers[idSite];
                        const camping = state.campings.find(c => String(c.idSite) === String(idSite));
                        if (camping && getDepartmentCode(camping) === state.currentDepartment) {
                            // Filtrer selon visibleCampingsIds si des filtres sont actifs
                            if (visibleIdsSet.size === 0 || visibleIdsSet.has(String(idSite))) {
                                if (!state.map.hasLayer(marker)) {
                                    marker.addTo(state.map);
                                }
                            } else {
                                // Masquer les marqueurs qui ne correspondent pas aux filtres
                                if (state.map.hasLayer(marker)) {
                                    state.map.removeLayer(marker);
                                }
                            }
                        } else {
                            // Masquer les marqueurs des autres départements
                            state.map.removeLayer(marker);
                        }
                    });
                } else {
                    // Des clusters existent, masquer seulement les pins qui font partie d'un cluster
                    // Les pins isolés doivent rester affichés
                    const campingIdsInClusters = new Set();
                    Object.values(state.pinClusters).forEach(clusterMarker => {
                        // Récupérer les IDs des campings dans les clusters depuis les données stockées
                        // On ne peut pas directement récupérer les campings depuis le marker, donc on doit les trouver autrement
                    });
                    
                    // Créer une liste des IDs des campings qui sont dans des clusters
                    // Pour cela, on doit recréer les clusters pour savoir quels pins sont dans des clusters
                    // IMPORTANT: Filtrer d'abord selon visibleCampingsIds si des filtres sont actifs
                    let deptCampings = state.campings.filter(c => getDepartmentCode(c) === state.currentDepartment);
                    if (visibleIdsSet.size > 0) {
                        deptCampings = deptCampings.filter(c => visibleIdsSet.has(String(c.idSite)));
                    }
                    
                    const campingsWithMarkers = deptCampings.filter(c => {
                        return state.markers[c.idSite] && c.gpsCoordinates && c.gpsCoordinates.latitude && c.gpsCoordinates.longitude;
                    });
                    
                    if (campingsWithMarkers.length > 0) {
                        const clusters = clusterPins(campingsWithMarkers, state.markers, 2000);
                        clusters.forEach(cluster => {
                            if (cluster.campings.length > 1) {
                                // Ce cluster contient plusieurs pins, donc ces pins ne doivent pas être affichés individuellement
                                cluster.campings.forEach(camping => {
                                    campingIdsInClusters.add(String(camping.idSite));
                                });
                            }
                        });
                    }
                    
                    // Masquer seulement les pins qui font partie d'un cluster
                    // Les pins isolés (qui ne sont pas dans campingIdsInClusters) doivent rester affichés
                    // MAIS filtrer selon visibleCampingsIds si des filtres sont actifs
                    Object.keys(state.markers).forEach(idSite => {
                        const marker = state.markers[idSite];
                        const camping = state.campings.find(c => String(c.idSite) === String(idSite));
                        
                        if (camping && getDepartmentCode(camping) === state.currentDepartment) {
                            // Vérifier d'abord si le camping correspond aux filtres
                            const matchesFilters = visibleIdsSet.size === 0 || visibleIdsSet.has(String(idSite));
                            
                            if (!matchesFilters) {
                                // Ne correspond pas aux filtres, masquer
                                if (state.map.hasLayer(marker)) {
                                    state.map.removeLayer(marker);
                                }
                            } else if (campingIdsInClusters.has(String(idSite))) {
                                // Ce pin fait partie d'un cluster, le masquer
                                if (state.map.hasLayer(marker)) {
                                    state.map.removeLayer(marker);
                                }
                            } else {
                                // Ce pin est isolé et correspond aux filtres, l'afficher
                                if (!state.map.hasLayer(marker)) {
                                    marker.addTo(state.map);
                                }
                            }
                        } else {
                            // Masquer les marqueurs des autres départements
                            state.map.removeLayer(marker);
                        }
                    });
                }
            } else {
                // Si visibleCampingsIds est vide (pas de filtres)
                // MAIS si un département est déjà sélectionné (via zoomToDepartment), ne rien faire
                // car zoomToDepartment gère déjà l'affichage des pins/clusters
                if (!state.currentDepartment) {
                    // Masquer tous les pins individuels d'abord
                    Object.keys(state.markers).forEach(idSite => {
                        const marker = state.markers[idSite];
                        if (state.map.hasLayer(marker)) {
                            state.map.removeLayer(marker);
                        }
                    });
                    
                    // Masquer tous les clusters de pins
                    if (state.pinClusters) {
                        Object.values(state.pinClusters).forEach(cluster => {
                            if (state.map.hasLayer(cluster)) {
                                state.map.removeLayer(cluster);
                            }
                        });
                        state.pinClusters = {};
                    }
                    
                    // Réafficher les clusters de départements avec le nombre total de campings
                    if (state.departmentClusters) {
                        Object.keys(state.departmentClusters).forEach(deptCode => {
                            const cluster = state.departmentClusters[deptCode];
                            // Compter tous les campings du département (pas de filtres)
                            const allCampings = state.campings.filter(c => getDepartmentCode(c) === deptCode);
                            const totalCount = allCampings.length;
                            
                            if (totalCount > 0) {
                                // Mettre à jour l'icône avec le nombre total
                                const clusterIcon = createDepartmentClusterIcon(totalCount);
                                cluster.setIcon(clusterIcon);
                                
                                // Mettre à jour le gestionnaire de clic pour utiliser tous les campings
                                cluster.off('click');
                                cluster.on('click', () => {
                                    store('gt/ctv').actions.zoomToDepartment(deptCode, allCampings);
                                });
                                
                                // S'assurer que le cluster est affiché
                                if (!state.map.hasLayer(cluster)) {
                                    cluster.addTo(state.map);
                                }
                            }
                        });
                    }
                    
                    // Réinitialiser le département sélectionné
                    state.currentDepartment = null;
                    state.departmentZoomLevel = null;
                    state.pinClusterZoomLevel = null;
                }
                // Si currentDepartment est défini, ne rien faire - zoomToDepartment gère l'affichage
            }
            
            // 2) Appliquer la visibilité selon visibleCampingsIds
            // IMPORTANT: Si une recherche est active, afficher directement les marqueurs individuels
            // Si des filtres sidebar sont actifs, les clusters sont déjà gérés plus haut
            if (isSearchActive && state.visibleCampingsIds.length > 0 && !state.currentDepartment) {
                // CAS RECHERCHE: Afficher directement les marqueurs individuels (pas de clusters)
                const visibleIdsSet = new Set((state.visibleCampingsIds || []).map(id => String(id)));
                
                Object.keys(state.markers).forEach(idSite => {
                    const marker = state.markers[idSite];
                    if (visibleIdsSet.has(String(idSite))) {
                        if (!state.map.hasLayer(marker)) {
                            marker.addTo(state.map);
                        }
                    } else {
                        if (state.map.hasLayer(marker)) {
                            state.map.removeLayer(marker);
                        }
                    }
                });
            } else if (!isSearchActive && !state.currentDepartment) {
                // CAS FILTRES SIDEBAR ou PAS DE FILTRES: Masquer les marqueurs individuels car on est au niveau des clusters
                // Les clusters de départements sont déjà gérés plus haut
                Object.keys(state.markers).forEach(idSite => {
                    const marker = state.markers[idSite];
                    if (state.map.hasLayer(marker)) {
                        state.map.removeLayer(marker);
                    }
                });
            }

            // 3) Ne pas ajuster automatiquement le zoom - respecter le zoom initial défini
            // Le zoom initial est géré par syncZoomFromContext() et ne doit pas être modifié ici

            // IMPORTANT: invalidateSize() peut déclencher un événement zoomend qui appelle handleZoomChange
            // Mais handleZoomChange devrait maintenant être désactivé quand des filtres sont actifs
            // On appelle invalidateSize() normalement car handleZoomChange vérifie les filtres
            setTimeout(() => {
                if (state.map) {
                    state.map.invalidateSize();
                }
            }, 200);
        },
        selectNextCamping: () => {
            const element = getElement();
            const campingsToUse = state.visibleCampingsIds
                ? state.campings.filter(c => state.visibleCampingsIds.includes(c.idSite.toString()))
                : state.campings;

            if (campingsToUse.length === 0) return;

            const currentIndex = campingsToUse.findIndex(c => c.idSite === state.selectedCamping?.idSite);
            const nextIndex = (currentIndex + 1) % campingsToUse.length;
            selectCampingAndUpdateUI(state, campingsToUse[nextIndex], element, state.map);
        },
        selectPreviousCamping: () => {
            const element = getElement();
            const campingsToUse = state.visibleCampingsIds
                ? state.campings.filter(c => state.visibleCampingsIds.includes(c.idSite.toString()))
                : state.campings;

            if (campingsToUse.length === 0) return;

            const currentIndex = campingsToUse.findIndex(c => c.idSite === state.selectedCamping?.idSite);
            const previousIndex = (currentIndex - 1 + campingsToUse.length) % campingsToUse.length;
            selectCampingAndUpdateUI(state, campingsToUse[previousIndex], element, state.map);
        },
        closeCampingDetails: () => {
            const element = getElement();
            const context = getContext();
            const queryElement = element.ref.closest('.wp-block-query');
            resetActiveElements(queryElement);
            state.selectedCamping = false;

            const maxZoom = 12;
            const minZoom = 5;
            const markerPositionsClose = Object.values(state.markers).map(marker => marker.getLatLng());
            if (markerPositionsClose.length > 0) {
                const bounds = L.latLngBounds(markerPositionsClose);
                // Utiliser maxZoom - 1 pour éviter les zooms trop serrés
                const fitMaxZoom = Math.max(maxZoom - 1, minZoom);
                state.map.fitBounds(bounds, { padding: [60, 60], maxZoom: fitMaxZoom, animate: true, duration: 0.4 });
            }
            setTimeout(() => state.map.invalidateSize(), 200);
        },
        toggleMap: () => { 
            const context = getContext();
            state.showMap = true;
            state.showList = false;
            state.selectedCamping = false;
    
            // Réinitialiser les classes actives
            const postTemplate = document.querySelector('.wp-block-post-template');
            if (postTemplate) postTemplate.classList.remove('active');
            document.querySelectorAll('[data-gt-camping]').forEach(el => el.classList.remove('active'));
    
            const maxZoom = 12;
            const minZoom = 5;
            // Récupérer les positions des marqueurs visibles
            const markerPositions = Object.values(state.markers).map(marker => marker.getLatLng());
            if (markerPositions.length > 0) {
                const bounds = L.latLngBounds(markerPositions);
                // Utiliser maxZoom - 1 pour éviter les zooms trop serrés
                const fitMaxZoom = Math.max(maxZoom - 1, minZoom);
                state.map.fitBounds(bounds, { padding: [100, 100], maxZoom: fitMaxZoom, animate: true, duration: 0.4 });
            }
    
            // Réinitialiser la taille de la carte après un délai
            setTimeout(() => state.map?.invalidateSize(), 200);

            state.isLoading = true;
            state.loaded = false;
            store('gt/ctv').actions.submitForm();
        },
        toggleList: () => {
            state.showList = true;
            state.showMap = false;
            state.selectedCamping = false;
            state.isLoading = true;
            state.loaded = false;

            const postTemplate = document.querySelector('.wp-block-post-template');
            if (postTemplate) postTemplate.classList.remove('active');
            document.querySelectorAll('[data-gt-camping]').forEach(el => el.classList.remove('active'));

            store('gt/ctv').actions.submitForm();
        },
        hoverCamping: (campingId) => {
            if (state.map && state.markers[campingId]) {
                
                // Récupérer le style original du marker
                const originalStyle = state.markerStyles[campingId];
                if (originalStyle) {
                    highlightMarker(state, campingId, originalStyle.color, originalStyle.style, originalStyle.hoverColor, state.map);
                } else {
                    highlightMarker(state, campingId, '#3498db', 'circle', '#e74c3c', state.map);
                }
            } else {
            }
        },
        unhoverCamping: (campingId) => {
            if (state.map && state.markers[campingId]) {
                
                // Récupérer le style original du marker
                const originalStyle = state.markerStyles[campingId];
                if (originalStyle) {
                    resetMarkerHighlight(state, campingId, originalStyle.color, originalStyle.style, state.map);
                } else {
                    resetMarkerHighlight(state, campingId, '#3498db', 'circle', state.map);
                }
            } else {
            }
        },
        // Synchroniser le zoom initial avec le contexte (appelé régulièrement pour détecter les changements)
        syncZoomFromContext: () => {
            if (!state.map) return;
            
            const context = getContext();
            const minZoom = 5;
            const maxZoom = 12;
            // Valider et contraindre le nouveau zoom initial (+ léger dézoom < 600px)
            const newInitialZoom = getResponsiveInitialZoom(context.initialZoom, minZoom, maxZoom);
            
            // Si le zoom a changé, mettre à jour le state et la carte
            if (state.currentInitialZoom !== newInitialZoom) {
                state.currentInitialZoom = newInitialZoom;
                // Utiliser le nouveau zoom initial exactement, sans ajustement automatique
                // Cela garantit que le zoom reste exactement à la valeur définie dans l'éditeur
                const currentCenter = state.map.getCenter();
                state.map.setView(currentCenter, newInitialZoom, { animate: true, duration: 0.4 });
            }
        },
        // Mettre à jour le zoom de la carte quand currentInitialZoom change dans le state
        updateZoomFromState: () => {
            if (!state.map || state.currentInitialZoom === null) return;
            
            const currentZoom = state.map.getZoom();
            const minZoom = state.minZoom || 5;
            const maxZoom = state.maxZoom || 12;
            // Valider le zoom initial avant de l'appliquer
            const validatedZoom = constrainZoom(state.currentInitialZoom, minZoom, maxZoom);
            
            // Si le zoom actuel est différent du zoom initial souhaité, mettre à jour avec animation fluide
            if (currentZoom !== validatedZoom) {
                const currentCenter = state.map.getCenter();
                state.map.setView(currentCenter, validatedZoom, { animate: true, duration: 0.4 });
            }
        },
        // Zoomer sur un département et afficher ses pins (ou clusters de pins si proches)
        zoomToDepartment: (deptCode, campings) => {
            
            if (!state.map) {
                console.error('state.map est null');
                return;
            }
            
            // PROTECTION: Éviter les appels multiples simultanés
            if (state.zoomingToDepartment === deptCode) {
                console.log('zoomToDepartment: Déjà en cours de zoom sur', deptCode, ', ignorer l\'appel');
                return;
            }
            
            state.zoomingToDepartment = deptCode;
            
            // CONSERVER visibleCampingsIds si des filtres sont actifs
            // Ne pas réinitialiser pour permettre le filtrage même après zoom sur département
            // state.visibleCampingsIds sera utilisé pour filtrer les pins affichés
            
            // Stocker le département sélectionné
            state.currentDepartment = deptCode;
            
            // Masquer tous les clusters de départements
            Object.values(state.departmentClusters || {}).forEach(cluster => {
                if (state.map.hasLayer(cluster)) {
                    state.map.removeLayer(cluster);
                }
            });
            
            // Masquer TOUS les marqueurs individuels et clusters de pins existants
            // C'est important pour éviter que les pins des clusters soient visibles
            Object.values(state.markers || {}).forEach(marker => {
                if (state.map.hasLayer(marker)) {
                    state.map.removeLayer(marker);
                }
            });
            Object.values(state.pinClusters || {}).forEach(cluster => {
                if (state.map.hasLayer(cluster)) {
                    state.map.removeLayer(cluster);
                }
            });
            state.pinClusters = {};
            
            // Afficher uniquement les marqueurs du département sélectionné
            // Si campings est fourni (avec GPS mis à jour), l'utiliser directement
            // Sinon, recalculer depuis state.campings (qui devrait avoir les GPS mis à jour)
            let deptCampings = campings;
            if (!deptCampings || deptCampings.length === 0) {
                deptCampings = state.campings ? state.campings.filter(c => getDepartmentCode(c) === deptCode) : [];
            }
            
            
            // Log des GPS dans deptCampings pour debug
            const campingsWithGPS = deptCampings.filter(c => c.gpsCoordinates && c.gpsCoordinates.latitude && c.gpsCoordinates.longitude);
           
            
            // Si aucun camping n'a de GPS, charger les GPS d'abord
            const campingsWithoutGPS = deptCampings.filter(c => 
                !c.gpsCoordinates || 
                c.gpsCoordinates.latitude === null || 
                c.gpsCoordinates.longitude === null ||
                c.gpsCoordinates.latitude === 0 ||
                c.gpsCoordinates.longitude === 0
            );
            
            // PROTECTION: Éviter de recharger les GPS si on est déjà en train de les charger
            if (campingsWithoutGPS.length > 0 && campingsWithoutGPS.length === deptCampings.length && !state.loadingGPS) {
                
                // Marquer qu'on est en train de charger les GPS
                state.loadingGPS = true;
                
                // Charger les GPS pour les campings du département
                const postIds = campingsWithoutGPS.map(c => c.postId).filter(Boolean);
                
                
                if (postIds.length > 0) {
                    const ajaxUrl = typeof ajaxurl !== 'undefined' ? ajaxurl : 
                                   (window.wp?.ajax?.url || '/wp-admin/admin-ajax.php');
                    
                    
                    fetch(ajaxUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams({
                            action: 'gt_ws_ctv_get_campings_gps',
                            post_ids: JSON.stringify(postIds),
                        }),
                    })
                    .then(response => {
                        console.log('zoomToDepartment: Réponse AJAX brute - status:', response.status, 'ok:', response.ok);
                        return response.text().then(text => {
                            console.log('zoomToDepartment: Réponse AJAX texte brute:', text.substring(0, 500));
                            try {
                                return JSON.parse(text);
                            } catch (e) {
                                console.error('zoomToDepartment: Erreur de parsing JSON:', e, 'Texte:', text);
                                throw e;
                            }
                        });
                    })
                    .then(data => {
                        // Réinitialiser le flag de chargement GPS
                        state.loadingGPS = false;
                   
                        
                        // Afficher les infos de débogage si disponibles
                        if (data.debug) {
                            console.log('zoomToDepartment: DEBUG INFO:', JSON.stringify(data.debug, null, 2));
                        }
                        
                        // Gérer le cas où data.data est directement un objet avec les résultats
                        // WordPress wp_send_json_success() retourne {success: true, data: {...}}
                        // Mais notre endpoint retourne maintenant {success: true, data: {...}, debug: [...]}
                        const gpsData = data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : (data.data?.data || data.data);
                    
                        
                        if (data.success && gpsData && typeof gpsData === 'object' && !Array.isArray(gpsData) && Object.keys(gpsData).length > 0) {
                            
                            let updatedCount = 0;
                            
                            // Mettre à jour les coordonnées GPS dans state.campings ET dans deptCampings
                            Object.keys(gpsData).forEach(postId => {
                                try {
                                    const gpsItem = gpsData[postId];
                                    
                                    if (!gpsItem || !gpsItem.latitude || !gpsItem.longitude) {
                                        console.warn('zoomToDepartment: GPS invalide pour postId', postId, ':', gpsItem);
                                        return;
                                    }
                                    
                                    
                                    // Mettre à jour dans state.campings
                                    if (state.campings && Array.isArray(state.campings)) {
                                        const campingInState = state.campings.find(c => 
                                            c && (c.postId == postId || 
                                            String(c.postId) === String(postId) ||
                                            c.idSite == gpsItem.campingKey ||
                                            String(c.idSite) === String(gpsItem.campingKey))
                                        );
                                        
                                        if (campingInState) {
                                            campingInState.gpsCoordinates = {
                                                latitude: parseFloat(gpsItem.latitude),
                                                longitude: parseFloat(gpsItem.longitude),
                                            };
                                            console.log('zoomToDepartment: GPS mis à jour dans state.campings pour camping', campingInState.idSite, ':', campingInState.gpsCoordinates);
                                            updatedCount++;
                                        } else {
                                            console.warn('zoomToDepartment: Camping non trouvé dans state.campings pour postId', postId, 'ou campingKey', gpsItem.campingKey);
                                        }
                                    }
                                    
                                    // Mettre à jour dans deptCampings aussi
                                    if (deptCampings && Array.isArray(deptCampings)) {
                                        const camping = deptCampings.find(c => 
                                            c && (c.postId == postId || 
                                            String(c.postId) === String(postId) ||
                                            c.idSite == gpsItem.campingKey ||
                                            String(c.idSite) === String(gpsItem.campingKey))
                                        );
                                        
                                        if (camping) {
                                            camping.gpsCoordinates = {
                                                latitude: parseFloat(gpsItem.latitude),
                                                longitude: parseFloat(gpsItem.longitude),
                                            };
                                            console.log('zoomToDepartment: GPS mis à jour dans deptCampings pour camping', camping.idSite, ':', camping.gpsCoordinates);
                                        } else {
                                            console.warn('zoomToDepartment: Camping non trouvé dans deptCampings pour postId', postId, 'ou campingKey', gpsItem.campingKey);
                                        }
                                    }
                                } catch (error) {
                                    console.error('zoomToDepartment: Erreur lors de la mise à jour GPS pour postId', postId, ':', error);
                                }
                            });
                            
                            
                            // Réinitialiser le flag avant de réappeler pour permettre le réappel
                            state.zoomingToDepartment = null;
                            state.loadingGPS = false;
                            console.log('zoomToDepartment: GPS chargés, réappel de zoomToDepartment');
                            store('gt/ctv').actions.zoomToDepartment(deptCode, deptCampings);
                        } else {
                            console.warn('zoomToDepartment: Aucun GPS chargé pour le département', deptCode);
                            console.warn('zoomToDepartment: data.success:', data.success);
                            console.warn('zoomToDepartment: gpsData:', gpsData);
                            console.warn('zoomToDepartment: Réponse complète:', data);
                            
                            // Afficher les infos de debug si disponibles
                            if (data.debug) {
                                console.warn('zoomToDepartment: DEBUG INFO:', JSON.stringify(data.debug, null, 2));
                            }
                            
                            // Réinitialiser les flags
                            state.zoomingToDepartment = null;
                            state.loadingGPS = false;
                            // Zoomer sur le centre du département même sans GPS
                            const center = getDepartmentCenter(deptCode);
                            if (center && center[0] && center[1]) {
                                state.map.setView(center, 9, { animate: true, duration: 0.4 });
                            }
                        }
                    })
                    .catch(error => {
                        console.error('zoomToDepartment: Erreur lors du chargement des GPS:', error);
                        // Réinitialiser les flags
                        state.loadingGPS = false;
                        state.zoomingToDepartment = null;
                        // Zoomer sur le centre du département même en cas d'erreur
                        const center = getDepartmentCenter(deptCode);
                        if (center && center[0] && center[1]) {
                            state.map.setView(center, 9, { animate: true, duration: 0.4 });
                        }
                    });
                    
                    return; // Retourner ici, la fonction sera réappelée après le chargement des GPS
                }
            }
            
            // Filtrer les campings qui ont des coordonnées GPS OU créer les marqueurs manquants
            const campingsWithMarkers = [];
            
            console.log('zoomToDepartment: Vérification des campings du département:', deptCampings.length);
            deptCampings.forEach((c, index) => {
                console.log(`zoomToDepartment: Camping ${index}:`, {
                    idSite: c.idSite,
                    postId: c.postId,
                    name: c.name,
                    hasGPS: !!(c.gpsCoordinates && c.gpsCoordinates.latitude && c.gpsCoordinates.longitude),
                    gps: c.gpsCoordinates
                });
            });
            
            deptCampings.forEach(c => {
                try {
                    const hasCoords = !!(c.gpsCoordinates && 
                                        c.gpsCoordinates.latitude && 
                                        c.gpsCoordinates.longitude &&
                                        c.gpsCoordinates.latitude !== 0 &&
                                        c.gpsCoordinates.longitude !== 0);
                    
                    
                    if (hasCoords) {
                        // Si le marqueur n'existe pas encore, le créer
                        if (!state.markers[c.idSite]) {
                            try {
                                
                                // Récupérer les styles depuis le state (stockés lors de l'initialisation) ou le contexte
                                let markerColor = state.markerColor || '#3498db'; // Utiliser la couleur du state si disponible
                                let markerStyle = state.markerStyle || 'circle'; // Utiliser le style du state si disponible
                                
                                // Si pas dans le state, essayer de récupérer depuis le contexte
                                if (!state.markerColor || !state.markerStyle) {
                                    try {
                                        if (typeof getContext === 'function') {
                                            const context = getContext();
                                            if (context && typeof context === 'object') {
                                                // Si markerColor est défini et non vide, l'utiliser
                                                // Sinon, utiliser accentColor, sinon la valeur par défaut
                                                if (!state.markerColor) {
                                                    if (context.markerColor && context.markerColor.trim() !== '') {
                                                        markerColor = context.markerColor;
                                                        state.markerColor = markerColor; // Stocker dans le state
                                                    } else if (context.accentColor && context.accentColor.trim() !== '') {
                                                        markerColor = context.accentColor;
                                                        state.markerColor = markerColor; // Stocker dans le state
                                                    }
                                                }
                                                if (!state.markerStyle && context.markerStyle) {
                                                    markerStyle = context.markerStyle;
                                                    state.markerStyle = markerStyle; // Stocker dans le state
                                                }
                                                console.log('zoomToDepartment: Couleur récupérée depuis contexte:', markerColor, 'Style:', markerStyle);
                                            }
                                        }
                                    } catch (error) {
                                        // Ignorer silencieusement et utiliser les valeurs par défaut
                                        console.warn('zoomToDepartment: Erreur getContext, utilisation valeurs par défaut:', error);
                                    }
                                } else {
                                    console.log('zoomToDepartment: Utilisation couleur depuis state:', markerColor, 'Style:', markerStyle);
                                }
                            
                            // Convertir les coordonnées en nombres pour s'assurer qu'elles sont valides
                            const lat = parseFloat(c.gpsCoordinates.latitude);
                            const lng = parseFloat(c.gpsCoordinates.longitude);
                            
                            if (isNaN(lat) || isNaN(lng)) {
                                console.error('zoomToDepartment: Coordonnées GPS invalides pour camping', c.idSite, ':', lat, lng);
                                return;
                            }
                            
                            const marker = L.marker([lat, lng], {
                                icon: L.divIcon({
                                    html: createModernSVGMarker(markerColor, 20, markerStyle),
                                    className: 'modern-svg-marker',
                                    iconSize: [40, 40],
                                    iconAnchor: getMarkerIconAnchor(markerStyle),
                                })
                            });
                            
                            // Construire la popup
                            let popupContent = '<div class="camping-popup-content">';
                            if (c.name) {
                                popupContent += `<div class="camping-popup-name"><b>${c.name}</b></div>`;
                            }
                            if (c.address || c.postalCode || c.city) {
                                popupContent += '<div class="camping-popup-address">';
                                if (c.address) {
                                    popupContent += `<div class="camping-popup-address-line">${c.address}</div>`;
                                }
                                if (c.postalCode || c.city) {
                                    const postalCity = [c.postalCode, c.city].filter(Boolean).join(' ');
                                    popupContent += `<div class="camping-popup-address-line">${postalCity}</div>`;
                                }
                                popupContent += '</div>';
                            }
                            if (c.url) {
                                popupContent += `<a href="${c.url}" class="camping-popup-link">${state.moreInfoLabel}</a>`;
                            }
                            popupContent += '</div>';
                            marker.bindPopup(popupContent);
                            enablePopupOnHover(marker, state, c.idSite);
                            
                            marker.on('click', () => {
                                state.selectedCamping = c;
                                try {
                                    if (typeof getElement === 'function') {
                                        const element = getElement();
                                        if (element && element.ref) {
                                            const queryElement = element.ref.parentNode;
                                            resetActiveElements(queryElement);
                                            activateCamping(queryElement, c.idSite);
                                            marker.openPopup();
                                            setTimeout(() => adjustMapHeight(state.map, queryElement), 200);
                                        } else {
                                            console.warn('zoomToDepartment: getElement() retourne undefined ou n\'a pas de ref');
                                            marker.openPopup();
                                        }
                                    } else {
                                        marker.openPopup();
                                    }
                                } catch (error) {
                                    console.error('zoomToDepartment: Erreur lors du clic sur le marqueur:', error);
                                    marker.openPopup();
                                }
                            });
                            
                            state.markers[c.idSite] = marker;
                            
                            // NE PAS ajouter le marqueur à la carte immédiatement
                            // Il sera ajouté plus tard selon qu'il est dans un cluster ou isolé
                            // Les clusters géreront l'affichage des pins
                            
                            campingsWithMarkers.push(c);
                        } catch (error) {
                            console.error('zoomToDepartment: Erreur lors de la création du marqueur pour camping', c.idSite, ':', error);
                            console.error('zoomToDepartment: Détails de l\'erreur:', error.message, error.stack);
                            // Continuer avec le camping suivant même en cas d'erreur
                        }
                    } else {
                        console.log('zoomToDepartment: Marqueur existe déjà pour camping', c.idSite);
                        // NE PAS ajouter le marqueur existant à la carte immédiatement
                        // Il sera ajouté plus tard selon qu'il est dans un cluster ou isolé
                        // Les clusters géreront l'affichage des pins
                        campingsWithMarkers.push(c);
                    }
                } else {
                    console.warn('zoomToDepartment: Camping', c.idSite, 'n\'a pas de coordonnées GPS valides - gpsCoordinates:', c.gpsCoordinates);
                }
                } catch (error) {
                    console.error('zoomToDepartment: Erreur lors du traitement du camping', c?.idSite || 'inconnu', ':', error);
                    console.error('zoomToDepartment: Détails de l\'erreur:', error.message, error.stack);
                    // Continuer avec le camping suivant même en cas d'erreur
                }
            });
            
         
            
            if (campingsWithMarkers.length === 0) {
                console.error('zoomToDepartment: Aucun camping valide trouvé avec GPS pour le département', deptCode);
                console.error('zoomToDepartment: deptCampings.length:', deptCampings.length);
                console.error('zoomToDepartment: Campings sans GPS:', deptCampings.filter(c => !c.gpsCoordinates || !c.gpsCoordinates.latitude || !c.gpsCoordinates.longitude).length);
                
                // Si on n'est pas en train de charger les GPS, zoomer sur le centre
                if (!state.loadingGPS) {
                    console.warn('zoomToDepartment: Zoom sur le centre du département', deptCode, 'car aucun camping n\'a de GPS');
                    const center = getDepartmentCenter(deptCode);
                    if (center && center[0] && center[1]) {
                        state.map.setView(center, 9, { animate: true, duration: 0.4 });
                    }
                    // Réinitialiser les flags
                    state.zoomingToDepartment = null;
                    state.loadingGPS = false;
                } else {
                    console.log('zoomToDepartment: En attente du chargement des GPS...');
                }
                return;
            }
            
           
            
            const markerPositions = [];
            
          
            
            // Créer des clusters de pins proches (distance de 2000 mètres)
            const clusters = clusterPins(campingsWithMarkers, state.markers, 2000);
            console.log('zoomToDepartment: Clusters créés:', clusters.length);
            
            // Récupérer la couleur depuis le state (stockée lors de l'initialisation)
            const markerColor = state.markerColor || '#3498db';
            const markerStyle = state.markerStyle || 'circle';
            console.log('zoomToDepartment: Utilisation couleur depuis state pour affichage:', markerColor, 'Style:', markerStyle);
            
            console.log('zoomToDepartment: Début traitement des clusters');
            clusters.forEach((cluster, index) => {
                console.log(`Cluster ${index}:`, cluster.campings.length, 'pins, center:', cluster.center);
                
                try {
                    if (cluster.campings.length > 1) {
                        // Créer UNIQUEMENT le cluster (cercle) pour plusieurs pins proches
                        // NE PAS ajouter les pins individuels à la carte ici
                        console.log('zoomToDepartment: Création cluster avec', cluster.campings.length, 'pins au centre', cluster.center);
                        const clusterIcon = createPinClusterIcon(cluster.campings.length, markerColor);
                        const clusterMarker = L.marker(cluster.center, { icon: clusterIcon });
                        if (!state.map.hasLayer(clusterMarker)) {
                            clusterMarker.addTo(state.map);
                            console.log('zoomToDepartment: Cluster ajouté à la carte');
                        } else {
                            console.log('zoomToDepartment: Cluster déjà sur la carte');
                        }
                        
                        clusterMarker.on('click', () => {
                            store('gt/ctv').actions.zoomToPinCluster(cluster.campings);
                        });
                        
                        const clusterId = `pin-cluster-${deptCode}-${index}`;
                        state.pinClusters[clusterId] = clusterMarker;
                        markerPositions.push(cluster.center);
                        console.log('Cluster ajouté:', clusterId, '- Les pins individuels ne sont PAS ajoutés');
                        
                        // IMPORTANT: S'assurer qu'aucun pin individuel de ce cluster n'est sur la carte
                        cluster.campings.forEach(camping => {
                            const marker = state.markers[camping.idSite];
                            if (marker && state.map.hasLayer(marker)) {
                                console.warn('Pin individuel trouvé sur la carte, retrait:', camping.idSite);
                                state.map.removeLayer(marker);
                            }
                        });
                    } else {
                        // Pin isolé (pas dans un cluster), l'ajouter directement
                        const camping = cluster.campings[0];
                        const marker = state.markers[camping.idSite];
                        if (marker) {
                            // Mettre à jour la couleur du marker si nécessaire
                            try {
                                marker.setIcon(L.divIcon({
                                    html: createModernSVGMarker(markerColor, 20, markerStyle),
                                    className: 'modern-svg-marker',
                                    iconSize: [40, 40],
                                    iconAnchor: getMarkerIconAnchor(markerStyle),
                                }));
                            } catch (error) {
                                console.warn('zoomToDepartment: Erreur lors de la mise à jour de l\'icône:', error);
                            }
                            
                            if (!state.map.hasLayer(marker)) {
                                marker.addTo(state.map);
                            } else {
                                console.log('zoomToDepartment: Pin isolé déjà sur la carte');
                            }
                            const { latitude, longitude } = camping.gpsCoordinates;
                            markerPositions.push([latitude, longitude]);
                            console.log('zoomToDepartment: Pin isolé position:', latitude, longitude);
                        } else {
                            console.warn('zoomToDepartment: Marqueur non trouvé pour pin isolé:', camping.idSite);
                            console.warn('zoomToDepartment: Marqueurs disponibles:', Object.keys(state.markers));
                        }
                    }
                } catch (error) {
                    console.error('Erreur dans le traitement du cluster', index, ':', error);
                }
            });
            
            // Vérification CRITIQUE : masquer TOUS les pins qui sont dans un cluster
            // Cette vérification doit être faite APRÈS la création des clusters
            const allClusteredIdsSet = new Set();
            clusters.forEach(cluster => {
                if (cluster.campings.length > 1) {
                    cluster.campings.forEach(camping => {
                        allClusteredIdsSet.add(String(camping.idSite));
                    });
                }
            });
            
            // Masquer tous les pins des clusters
            allClusteredIdsSet.forEach(campingId => {
                const marker = state.markers[campingId];
                if (marker && state.map.hasLayer(marker)) {
                    console.warn('zoomToDepartment: Pin de cluster détecté et masqué:', campingId);
                    state.map.removeLayer(marker);
                }
            });
            
            // Vérification finale : s'assurer que tous les marqueurs isolés sont sur la carte
            // (ceux qui ne sont pas dans un cluster avec plusieurs campings)
            // IMPORTANT: Ne PAS réafficher les pins qui sont dans un cluster
            const allClusteredIds = new Set();
            clusters.forEach(cluster => {
                if (cluster.campings.length > 1) {
                    cluster.campings.forEach(camping => {
                        allClusteredIds.add(String(camping.idSite));
                    });
                }
            });
            
            // S'assurer que les pins des clusters sont bien masqués
            allClusteredIds.forEach(campingId => {
                const marker = state.markers[campingId];
                if (marker && state.map.hasLayer(marker)) {
                    console.warn('zoomToDepartment: Pin de cluster trouvé sur la carte, masquage:', campingId);
                    state.map.removeLayer(marker);
                }
            });
            
            // Ajouter uniquement les pins isolés (pas dans un cluster)
            campingsWithMarkers.forEach(camping => {
                if (!allClusteredIds.has(String(camping.idSite))) {
                    const marker = state.markers[camping.idSite];
                    if (marker && !state.map.hasLayer(marker)) {
                        // Mettre à jour la couleur du marker si nécessaire
                        try {
                            marker.setIcon(L.divIcon({
                                html: createModernSVGMarker(markerColor, 20, markerStyle),
                                className: 'modern-svg-marker',
                                iconSize: [40, 40],
                                iconAnchor: getMarkerIconAnchor(markerStyle),
                            }));
                        } catch (error) {
                            console.warn('zoomToDepartment: Erreur lors de la mise à jour de l\'icône:', error);
                        }
                        marker.addTo(state.map);
                    }
                } else {
                    // Ce pin est dans un cluster, s'assurer qu'il est masqué
                    const marker = state.markers[camping.idSite];
                    if (marker && state.map.hasLayer(marker)) {
                        console.warn('zoomToDepartment: Pin de cluster encore visible, masquage:', camping.idSite);
                        state.map.removeLayer(marker);
                    }
                }
            });
            
            
            // Vérification finale : compter les marqueurs sur la carte
            let markersOnMap = 0;
            const markersOnMapIds = [];
            Object.keys(state.markers || {}).forEach(idSite => {
                const marker = state.markers[idSite];
                if (marker && state.map.hasLayer(marker)) {
                    markersOnMap++;
                    markersOnMapIds.push(idSite);
                }
            });
            
            // S'assurer que TOUS les markers isolés du département sont sur la carte
            // IMPORTANT: Ne PAS ajouter les markers qui sont dans un cluster
            let missingMarkers = 0;
            campingsWithMarkers.forEach(camping => {
                // Ne pas ajouter les markers qui sont dans un cluster
                if (!allClusteredIds.has(String(camping.idSite))) {
                    const marker = state.markers[camping.idSite];
                    if (marker && !state.map.hasLayer(marker)) {
                        marker.addTo(state.map);
                        missingMarkers++;
                    }
                } else {
                    // Ce marker est dans un cluster, s'assurer qu'il est masqué
                    const marker = state.markers[camping.idSite];
                    if (marker && state.map.hasLayer(marker)) {
                        state.map.removeLayer(marker);
                    }
                }
            });
            if (missingMarkers > 0) {
                console.log('zoomToDepartment: Markers isolés manquants ajoutés:', missingMarkers);
            }
            
            // Zoomer sur les marqueurs/clusters du département
            if (markerPositions.length > 0) {
                const bounds = L.latLngBounds(markerPositions);
                const minZoom = 5;
                const maxZoom = 12;
                const fitMaxZoom = Math.max(maxZoom - 1, minZoom);
                
                console.log('Zoom sur bounds');
                state.map.fitBounds(bounds, { padding: [60, 60], maxZoom: fitMaxZoom, animate: true, duration: 0.4 });
                
                // Stocker le zoom après le fitBounds (avec un petit délai pour laisser l'animation se terminer)
                setTimeout(() => {
                    if (state.map) {
                        state.departmentZoomLevel = state.map.getZoom();
                    }
                }, 500);
            } else {
                console.warn('Aucune position de marqueur');
            }
            
            state.currentDepartment = deptCode;
            state.pinClusterZoomLevel = null;
            
            // Réinitialiser le flag de zoom en cours
            state.zoomingToDepartment = null;
            
            // S'assurer que les clusters de départements restent masqués
            // (au cas où updateMap serait appelé après)
            Object.values(state.departmentClusters || {}).forEach(cluster => {
                if (state.map.hasLayer(cluster)) {
                    state.map.removeLayer(cluster);
                }
            });
        },
        // Zoomer sur un cluster de pins pour afficher les pins individuels
        zoomToPinCluster: (campings) => {
            if (!state.map) return;
            
            // Masquer TOUS les clusters de pins (y compris celui sur lequel on a cliqué)
            Object.values(state.pinClusters).forEach(cluster => {
                if (state.map.hasLayer(cluster)) {
                    state.map.removeLayer(cluster);
                }
            });
            
            // Masquer TOUS les marqueurs individuels existants (y compris les pins isolés du département)
            Object.values(state.markers).forEach(marker => {
                if (state.map.hasLayer(marker)) {
                    state.map.removeLayer(marker);
                }
            });
            
            // Afficher UNIQUEMENT les pins individuels du cluster cliqué
            const markerPositions = [];
            campings.forEach(camping => {
                const marker = state.markers[camping.idSite];
                if (marker) {
                    marker.addTo(state.map);
                    const { latitude, longitude } = camping.gpsCoordinates;
                    markerPositions.push([latitude, longitude]);
                }
            });
            
            // Zoomer sur les pins individuels du cluster
            if (markerPositions.length > 0) {
                const bounds = L.latLngBounds(markerPositions);
                const minZoom = 5;
                const maxZoom = 12;
                const fitMaxZoom = Math.max(maxZoom - 0.5, minZoom);
                
                state.map.fitBounds(bounds, { padding: [60, 60], maxZoom: fitMaxZoom, animate: true, duration: 0.4 });
                
                // Stocker le zoom après le fitBounds
                setTimeout(() => {
                    if (state.map) {
                        state.pinClusterZoomLevel = state.map.getZoom();
                    }
                }, 500);
            }
        },
        // Revenir à la vue départements (clusters)
        resetToDepartmentView: () => {
            if (!state.map) return;
            
            // Masquer tous les marqueurs individuels et clusters de pins
            Object.values(state.markers).forEach(marker => {
                if (state.map.hasLayer(marker)) {
                    state.map.removeLayer(marker);
                }
            });
            Object.values(state.pinClusters).forEach(cluster => {
                if (state.map.hasLayer(cluster)) {
                    state.map.removeLayer(cluster);
                }
            });
            state.pinClusters = {};
            
            // Réafficher tous les clusters de départements
            Object.values(state.departmentClusters).forEach(cluster => {
                if (!state.map.hasLayer(cluster)) {
                    cluster.addTo(state.map);
                }
            });
            
            state.currentDepartment = null;
            state.departmentZoomLevel = null;
            state.pinClusterZoomLevel = null;
            
            // Réinitialiser le zoom à la vue initiale
            const minZoom = 5;
            const maxZoom = 12;
            const initialZoom = state.currentInitialZoom || 6;
            const center = [48.5, 6.0]; // Centre approximatif de la région Grand Est
            state.map.setView(center, initialZoom, { animate: true, duration: 0.4 });
        },
        // Gérer le changement de zoom pour basculer entre clusters et pins
        handleZoomChange: () => {
            if (!state.map) return;

          
          
            
            // IMPORTANT: Si des filtres sont actifs (visibleCampingsIds ne contient pas TOUS les campings),
            // ne pas réafficher tous les clusters. Laisser updateMap gérer l'affichage selon les filtres
            const totalCampings = state.campings ? state.campings.length : 0;
            const visibleCount = state.visibleCampingsIds ? state.visibleCampingsIds.length : 0;
            const filtersActive = totalCampings > 0 && visibleCount > 0 && visibleCount < totalCampings;
            
            if (filtersActive) {
                // Des filtres sont actifs (pas tous les campings sont visibles), ne pas interférer avec updateMap
                console.log('handleZoomChange: Filtres actifs (', visibleCount, '/', totalCampings, 'campings visibles), arrêt pour éviter de réafficher les clusters');
                return;
            }
            
            
            const currentZoom = state.map.getZoom();
            
            // Gérer l'opacité des éléments avec la classe text-carte selon le zoom
            if (state.previousZoom !== null) {
                const textCarteElements = document.querySelectorAll('.text-carte');
                if (currentZoom > state.previousZoom) {
                    // On zoome : réduire l'opacité
                    textCarteElements.forEach(el => {
                        el.style.opacity = '0';
                        el.style.visibility = 'hidden';
                    });
                } else if (currentZoom < state.previousZoom) {
                    // On dézoome : remettre l'opacité à 1
                    textCarteElements.forEach(el => {
                        el.style.opacity = '1';
                        el.style.visibility = 'visible';
                    });
                }
            }
            
            // Mettre à jour le zoom précédent
            state.previousZoom = currentZoom;
            
            // Si on a zoomé sur un cluster de pins, vérifier si on dézoome
            if (state.pinClusterZoomLevel !== null) {
                const zoomDifference = state.pinClusterZoomLevel - currentZoom;
                if (zoomDifference >= 1.5) {
                    // Masquer tous les pins individuels
                    Object.values(state.markers).forEach(marker => {
                        if (state.map.hasLayer(marker)) {
                            state.map.removeLayer(marker);
                        }
                    });
                    
                    // Nettoyer les clusters de pins existants
                    Object.values(state.pinClusters).forEach(cluster => {
                        if (state.map.hasLayer(cluster)) {
                            state.map.removeLayer(cluster);
                        }
                    });
                    state.pinClusters = {};
                    
                    // Recréer les clusters de pins pour le département
                    if (state.currentDepartment && state.campings) {
                        let deptCampings = state.campings.filter(c => getDepartmentCode(c) === state.currentDepartment);
                        
                        // Filtrer selon visibleCampingsIds si des filtres sont actifs
                        if (state.visibleCampingsIds && state.visibleCampingsIds.length > 0) {
                            const visibleIdsSet = new Set((state.visibleCampingsIds || []).map(id => String(id)));
                            deptCampings = deptCampings.filter(c => visibleIdsSet.has(String(c.idSite)));
                        }
                        
                        const campingsWithMarkers = deptCampings.filter(c => {
                            return state.markers[c.idSite] && c.gpsCoordinates && c.gpsCoordinates.latitude && c.gpsCoordinates.longitude;
                        });
                        
                        if (campingsWithMarkers.length > 0) {
                            let markerColor = '#3498db';
                            try {
                                const context = getContext();
                                markerColor = context.markerColor || (context.accentColor || '#3498db');
                            } catch (error) {
                                // Utiliser la couleur par défaut
                            }
                            
                            const clusters = clusterPins(campingsWithMarkers, state.markers, 2000);
                            
                            clusters.forEach((cluster, index) => {
                                if (cluster.campings.length > 1) {
                                    const clusterIcon = createPinClusterIcon(cluster.campings.length, markerColor);
                                    const clusterMarker = L.marker(cluster.center, { icon: clusterIcon }).addTo(state.map);
                                    
                                    clusterMarker.on('click', () => {
                                        store('gt/ctv').actions.zoomToPinCluster(cluster.campings);
                                    });
                                    
                                    const clusterId = `pin-cluster-${state.currentDepartment}-${index}`;
                                    state.pinClusters[clusterId] = clusterMarker;
                                } else {
                                    const camping = cluster.campings[0];
                                    const marker = state.markers[camping.idSite];
                                    if (marker) {
                                        marker.addTo(state.map);
                                    }
                                }
                            });
                        }
                    }
                    
                    state.pinClusterZoomLevel = null;
                    return; // Ne pas continuer avec les autres vérifications
                }
            }
            
            // Si un département est sélectionné, vérifier si on a dézoomé significativement
            if (state.currentDepartment && state.departmentZoomLevel !== null) {
                const zoomDifference = state.departmentZoomLevel - currentZoom;
                if (zoomDifference >= 1.5) {
                    // Masquer tous les pins et clusters de pins du département
                    Object.values(state.markers).forEach(marker => {
                        if (state.map.hasLayer(marker)) {
                            state.map.removeLayer(marker);
                        }
                    });
                    Object.values(state.pinClusters).forEach(cluster => {
                        if (state.map.hasLayer(cluster)) {
                            state.map.removeLayer(cluster);
                        }
                    });
                    state.pinClusters = {};
                    
                    // Réinitialiser le département sélectionné
                    state.currentDepartment = null;
                    state.departmentZoomLevel = null;
                    state.pinClusterZoomLevel = null;
                    
                    // Appeler updateMap pour mettre à jour les clusters selon les filtres actifs
                    if (store('gt/ctv')?.actions?.updateMap) {
                        store('gt/ctv').actions.updateMap();
                    }
                    return; // Ne pas continuer
                }
            }
            
            // Si aucun département n'est sélectionné, s'assurer que les clusters de départements sont visibles
            // MAIS seulement ceux qui correspondent aux filtres actifs
            // IMPORTANT: Cette section ne devrait JAMAIS être exécutée si des filtres sont actifs
            // car on retourne au début de la fonction si visibleCampingsIds.length > 0
            if (!state.currentDepartment) {
                // Masquer tous les marqueurs individuels et clusters de pins
                Object.values(state.markers).forEach(marker => {
                    if (state.map.hasLayer(marker)) {
                        state.map.removeLayer(marker);
                    }
                });
                Object.values(state.pinClusters).forEach(cluster => {
                    if (state.map.hasLayer(cluster)) {
                        state.map.removeLayer(cluster);
                    }
                });
                state.pinClusters = {};
                
                // Réafficher tous les clusters de départements
                // (on a déjà vérifié au début de la fonction que filtersActive est false)
                if (state.departmentClusters) {
                    Object.values(state.departmentClusters).forEach(cluster => {
                        if (!state.map.hasLayer(cluster)) {
                            cluster.addTo(state.map);
                        }
                    });
                }
            }
        },
        // Fonction de diagnostic pour déboguer les problèmes
        debugMapState: () => {
            if (state.campings) {
            }
        }
    }
});

async function updateUrl() {
    const router = await import('@wordpress/interactivity-router');

    const url = new URL(window.location);
    url.searchParams.set('arrival', state.gtArrival);
    url.searchParams.set('departure', state.gtDeparture);
    url.searchParams.set('nbPers', state.gtNbPers);

    await router.actions.navigate(url.toString(), {});
}
